import type { AppMessages } from "./zh-CN";

/**
 * English catalog. Must mirror `zh-CN` key-for-key (enforced by the type and by
 * `i18n/catalog.test.ts`). Brand names and tech abbreviations stay untranslated.
 */
export const enUS: AppMessages = {
  qaeCollection: {"startInstructions": "In QAE, open a completed chat answer as a tenant administrator and click its green sync button. Review the question, answer and tool records here before saving.", "openSource": "Open QAE ({host})", "backToBank": "Back To Project Question Bank", "unavailable": "Question collection is unavailable for this account or project.", "sourcesFailed": "Could not load the QAE sources. Refresh to retry.", "title": "Question Collection", "description": "Review the complete QAE answer and evidence before saving to this project.", "expired": "The collection link is missing or expired. Return to QAE and click the green action again.", "saved": "Question Saved", "savedDescription": "The complete question, answer and tool evidence were saved in a new test suite.", "viewQuestion": "View Saved Question", "loading": "Loading the complete answer and tool evidence…", "context": "Select the preceding turns required to reproduce this question", "round": "Turn {round}", "contextRound": "Context Turn {round}", "target": "Destination Project", "question": "Current Question", "answer": "Complete Standard Answer", "files": "Attachments And File References", "baseSuite": "Include Questions From An Existing Suite", "noBaseSuite": "Use Only This Question", "suiteName": "New Test Suite Name", "reviewed": "I have reviewed the project, question, complete answer and tool evidence", "saving": "Saving…", "save": "Confirm And Save To Question Bank", "tenant": "Source Tenant", "tools": "Complete Tool And Source Evidence"},
  qaeSync: { syncedSteps: "Original QAE Tool Steps", evidence: "Synchronization Source Evidence" },
  language: {
    label: "Language",
    switchAria: "Switch Interface Language",
    "zh-CN": "简体中文",
    "en-US": "English",
    "de-DE": "Deutsch",
  },
  common: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    edit: "Edit",
    delete: "Delete",
    remove: "Remove",
    create: "Create",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    retryLater: "Please try again later.",
    requestFailedRetry: "The request failed. Try again later",
    project: "Project",
    suite: "Suite",
    type: "Type",
    duration: "Duration",
    status: "Status",
    createdAt: "Created At",
    updatedTime: "Updated At",
    updatedAt: "Updated At {time}",
    sortCreatedDesc: "Created Time, Newest First",
    reload: "Reload",
    actions: "Actions",
    retry: "Retry",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    reset: "Reset",
    back: "Back",
    copy: "Copy",
    copied: "Copied",
    copyFailed: "Copy Failed",
    loading: "Loading…",
    empty: "No Data Yet",
    unknown: "Unknown",
    none: "None",
    all: "All",
    yes: "Yes",
    no: "No",
    enabled: "Enabled",
    disabled: "Disabled",
    optional: "Optional",
    required: "Required",
    detail: "Details",
    more: "More",
    apply: "Apply",
    submit: "Submit",
    export: "Export",
    import: "Import",
    upload: "Upload",
    download: "Download",
    view: "View",
    open: "Open",
    add: "Add",
    ok: "OK",
    unnamed: "Untitled",
    dash: "-",
    loadFailed: "Failed To Load",
    networkError: "Network Error, Please Try Again Later",
    unknownError: "Unknown Error",
    errorPrefix: "Error: {message}",
    requestFailed: "Request Failed ({status})",
    clearSearch: "Clear Search",
    caseId: "Question ID",
    caseIdRequired: "Question ID *",
    case: "Question",
    owner: "Owner",
    stage: "Stage",
    agent: "AI Engineer",
    connection: "Connection",
    conversation: "Conversation",
    revision: "Revision",
    columnNumber: "Column {index}",
    resizeColumn: "Resize {column} Column",
    resizeColumnHint: "Drag To Resize; Use Arrow Keys To Adjust; Press Enter, Home, Or Double-Click To Reset",
  },
  format: {
    durationMinutes: "{value} min",
  },
  status: {
    unknown: "Unknown",
    unknownWithValue: "Unknown ({value})",
    run: {
      queued: "Not Started",
      validating: "Not Started",
      running: "In Progress",
      judging: "In Progress",
      completed_pending_sync: "In Progress",
      completed: "Completed",
      failed: "Failed",
      stopping: "In Progress",
      stopped: "Stopped",
      cancelled: "Stopped",
      validationFailed: "Validation Failed",
      completedIncomplete: "Completed",
    },
    runFilter: {
      running: "Running",
      active: "In Progress",
    },
    cross: {
      queued: "Queued", creating_task: "Creating Collection Task", dispatched: "Dispatched", running: "Collecting", parsing: "Parsing",
      captured: "Captured", captured_partial: "Partially Captured", evaluating: "Evaluating", completed: "Completed",
      completed_with_warnings: "Completed With Warnings", partial_failed: "Partially Failed", retry_wait: "Waiting To Retry", failed: "Failed", stopping: "Stopping", stopped: "Stopped",
    },
    governance: {
      draft: "Draft",
      active: "Active",
      archived: "Archived",
      candidate: "Candidate",
      blocked: "Blocked",
      passed: "Passed",
      passed_with_warnings: "Passed (With Warnings)",
      released: "Released",
      pending: "Pending",
      editing: "Editing",
      promoted: "Promoted",
      rejected: "Rejected",
      duplicate: "Duplicate",
      succeeded: "Succeeded",
      partial_success: "Partial Success",
      available: "Available",
      unavailable: "Unavailable",
      unknown: "Unknown",
      error: "Error",
      delivered: "Delivered",
      retrying: "Retrying",
      dead_letter: "Dead Letter",
      disabled: "Disabled",
      shadow: "Shadow Mode",
      enforcing: "Enforcing",
    },
    candidate: {
      pending: "To Process",
      editing: "Editing",
      promoted: "Promoted",
      rejected: "Rejected",
      duplicate: "Duplicate",
    },
    syncItem: {
      pending: "Pending",
      running: "Processing",
      imported: "Imported",
      skipped: "Skipped",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    suiteType: {
      release_smoke: "Release Smoke",
      capability_quick: "Capability Quick Check",
      region_compatibility: "Region Compatibility",
      incident_regression: "Incident Regression",
      security_regression: "Security Regression",
      full_regression: "Full Regression",
      temporary: "Temporary Suite",
    },
    skipReason: {
      question_disabled: "Question Disabled",
      question_archived: "Question Archived",
      suite_item_disabled: "Suite Question Disabled",
    },
    projectType: {
      agent: "AI Engineer",
      workflow: "Workflow",
      api: "API",
      browser_agent: "Browser AI Engineer",
    },
    evaluatorKind: {
      llm_judge: "LLM Judge",
      rule_based: "Rule Based",
      hybrid: "Hybrid",
      manual: "Manual",
    },
    logLevel: {
      debug: "Debug",
      info: "Info",
      warn: "Warning",
      warning: "Warning",
      error: "Error",
      critical: "Critical",
      fatal: "Critical",
    },
    stage: {
      dev: "Dev",
      qa: "QA",
      staging: "Staging",
      production: "Production",
      custom: "Custom",
    },
  },
  nav: {
    overview: "Overview",
    projects: "Projects",
    newRun: "New Test Task",
    questionBank: "All Question Banks",
    suites: "All Test Suites",
    queue: "All Test Tasks",
    reports: "All Test Reports",
    crossEvaluations: "Cross Evaluation",
    logs: "Logs",
    settings: "Settings",
  },
  route: {
    parent: {
      runs: "Runs",
      tasks: "Test Tasks",
      results: "Test Results",
      reports: "Test Reports",
      queue: "Run Queue",
    },
  },
  shell: {
    documentTitle: "{page} · Agent Judge",
    brandSubtitle: "AI Engineer Testing Platform",
    brandAria: "Agent Judge Testing Workspace",
    primaryNavAria: "Primary Navigation",
    breadcrumbAria: "Breadcrumb",
    navGroup: { workspace: "Workspace", testAssets: "Test Assets", crossProject: "Across Projects" },
    skipToContent: "Skip To Main Content",
    topbarContext: "AI Engineer Testing Workspace",
    collapse: "Collapse",
    collapseSidebar: "Collapse Sidebar",
    expandSidebar: "Expand Sidebar",
    accountSecurity: "Account Security",
    logout: "Sign Out",
    loggingOut: "Signing out…",
    logoutFailed: "Sign-Out Failed, You Are Still Signed In: {message}",
    logoutFailedFallback: "Check Your Network And Try Again",
    routeLoading: "Loading page…",
    title: {
      overview: "Overview",
      evaluationProfiles: "Evaluation Profiles",
      prompts: "Scoring Prompts",
      targets: "Run Targets",
      projectQuestions: "Question Bank",
      productionSamples: "Production Conversations",
      dataSync: "Data Sync",
      projectContent: "Test Suites",
      projectRuns: "Runs",
      projectTasks: "Test Tasks",
      projectResults: "Test Results",
      projectReports: "Test Reports",
      coverage: "Capability Coverage",
      gatePolicy: "Gate Policy",
      gateNotifications: "Gate Notifications",
      projectSettings: "Project Settings",
      projects: "Projects",
      reports: "All Test Reports",
      questionBank: "All Question Banks",
      suites: "All Test Suites",
      queue: "All Test Tasks",
      logs: "Logs",
      security: "Account Security",
      migrated: "Settings Moved",
      settings: "Settings",
      newRun: "New Test Task",
      failures: "Failure Review",
      runDetail: "Run Detail",
      crossEvaluations: "Cross Evaluation",
      notFound: "Page Not Found",
    },
  },
  api: {
    networkFailed: "Network Request Failed",
    downloadNetworkFailed: "Network Request Failed, Cannot Download The File",
  },
  auth: {
    workspaceLabel: "AI Evaluation Workspace",
    kicker: "INDUSTRIALMIND EVALUATION",
    poweredBy: "Powered By IndustrialMind AI Evaluation Platform",
    headlineLead: "Make Every AI Engineer Change",
    headlineEmphasis: "Stand Up To Evaluation",
    headlineDescription: "From question bank and suites to run execution, Judge scoring and report analysis — one workspace for a reproducible quality loop.",
    flowAria: "AI Engineer Evaluation Flow",
    formPanelAria: "Agent Judge Sign In",
    eyebrow: "Secure Evaluation Workspace",
    title: "Welcome Back",
    description: "Sign in to keep managing evaluation runs and quality reports.",
    bootstrapEyebrow: "First-Time Setup",
    bootstrapTitle: "Initialize Administrator",
    bootstrapDescription: "Create the first administrator account in the local database.",
    platformEyebrow: "Unified Identity",
    platformTitle: "Sign In With IM Production",
    platformDescription: "Agent Judge reuses your current IM Production account and tenant identity.",
    platformGuidance: "If you are not signed in, complete IM Production sign-in in the new tab. This page will check again automatically.",
    platformOpenLogin: "Open IM Production Sign-In",
    platformRecheck: "Check Sign-In Again",
    platformTrustNote: "IM Production verifies your identity; Agent Judge does not store the access token",
    platformSessionExpired: "Your IM Production session expired. Please sign in again",
    platformAccessDenied: "The current IM Production tenant cannot access Agent Judge. Switch to an authorized tenant",
    username: "Username",
    password: "Password",
    showPassword: "Show Password",
    hidePassword: "Hide Password",
    verifying: "Verifying…",
    submit: "Sign In To Agent Judge",
    bootstrapSubmit: "Create Administrator And Continue",
    trustNote: "Credentials are only used for local secure session authentication",
    loginFailed: "Sign-In Failed, Please Try Again",
    sessionExpired: "Your Session Expired, Please Sign In Again",
    step: {
      questionBank: "Question Bank",
      suites: "Suites",
      runs: "Runs",
      reports: "Test Reports",
    },
    gate: {
      checking: "Checking Sign-In Status",
      pleaseWait: "Please wait…",
      unreachableTitle: "Cannot Reach The Agent Judge Backend",
      unreachable: "The Agent Judge backend is temporarily unreachable.",
      offline: "Network connection lost. Check your local network, or confirm the Agent Judge backend is still running.",
      reconnect: "Reconnect",
    },
  },
  confirm: {
    pending: "Working…",
  },
  guard: {
    discardTitle: "Discard Unsaved Changes?",
    discardMessage: "You have unsaved changes. Leaving will lose this draft.",
    stayEditing: "Stay And Keep Editing",
    discardAndLeave: "Discard And Leave",
  },
  pager: {
    aria: "Pagination",
    summary: "Page {page} Of {totalPages} · {total} Items",
    total: "{total} items",
    page: "{page} / {totalPages}",
    pageSize: "Rows",
    pageSizeAria: "Rows Per Page",
    previous: "Previous",
    next: "Next",
  },
  governance: {
    copyId: "Copy ID",
    copyIdAria: "{label}: {value}",
    idCopied: "ID Copied",
    copyIdFailed: "Copy Failed, Please Select It Manually",
    readOnlyTitle: "Read-Only Project",
    readOnlyDescription: "This project is for reviewing historical assets and run records only; content cannot be added or edited.",
    metricAria: "{label}: {value}",
    tabsAria: "Project Navigation",
    settingsNavAria: "Project Settings Sections",
    unsavedMarker: "Unsaved Changes",
    tab: {
      overview: "Project Overview",
      content: "Test Suites",
      productionSamples: "Production Conversations",
      runs: "Runs",
      tasks: "Test Tasks",
      results: "Test Results",
      reports: "Test Reports",
      settings: "Settings",
    },
    settingsNav: {
      defaults: "Project Defaults",
      env: "Run Targets",
      eval: "Evaluation Profiles",
      prompts: "Scoring Prompts",
      sync: "Data Sync",
    },
  },
  settingsReadiness: {
    env: {
      label: "Run Target",
      ready: "{count} Active Run Target(S)",
      missing: "At Least One Active Run Target Is Required",
    },
    defaultProfile: {
      label: "Default Evaluation Profile",
      ready: "Set To: {name}",
      missing: "Not set; new evaluations will not preselect a profile",
    },
    judgeKey: {
      label: "Judge Model Credentials",
      needProfile: "Set A Default Evaluation Profile First",
      ready: "Service URL And Key Are Configured",
      missingUrl: "Judge Model Service URL Is Missing",
      missingKey: "Judge Model Key Is Missing",
    },
    prompt: {
      label: "Scoring Prompts",
      referenced: "Default Profile References: {name}",
      referencedUnnamed: "A Prompt Reference Is Set",
      optional: "System Or Project Prompts Can Be Used (Optional)",
      none: "No project prompt yet; runs can use the system default",
    },
  },
  importSteps: {
    aria: "Import Steps",
    parse: "Parse File",
    conflicts: "Check Conflicts",
    commit: "Confirm And Write",
  },
  notFound: {
    title: "Page Not Found",
    description: "There is no page for this address — the link may have expired or the address may be mistyped.",
    goBack: "Go Back",
    goHome: "Back To Evaluation Home",
  },
  migrationPlaceholder: {
    description: "This page is being migrated to React; backend APIs and database schema are unchanged.",
  },
  logs: {
    redactedPlaceholder: "[redacted]",
    circularPlaceholder: "[Circular Reference]",
  },
  runs: {
    sortAria: "{label}, {state}",
    sortAsc: "Ascending",
    sortDesc: "Descending",
    sortIdle: "Click To Sort",
    duration: {
      seconds: "{value} s",
      minutes: "{value} min",
      hours: "{value} h",
    },
    progress: {
      aria: "Run Progress {percent}%",
      notStarted: "Not Started",
      notStartedAria: "Run Has Not Started, {total} Steps Planned",
    },
    actions: {
      requestFailed: "Request Failed, Please Try Again Later",
      stopped: "Run Stopped",
      stopFailed: "Failed To Stop The Run: {message}",
      resumed: "Resume Request Submitted",
      resumeFailed: "Failed To Resume The Run: {message}",
      deleted: "Deleted Run “{name}”",
      deleteFailed: "Failed To Delete The Run: {message}",
    },
  },
  runDetail: {
    knowledgeSourceFallback: "Knowledge Base Source",
    sourceDocuments: "Source Documents ({count})",
    answerLoading: "Preparing the answer…",
    turnCountSuffix: " ({count} turns)",
    skipReasonFallback: "Not Runnable When The Run Was Created",
    judgePromptFallbackName: "Evaluation Prompt",
    alignment: {
      match: "Match",
      mismatch: "Mismatch",
    },
    integrity: {
      placeholderAnswer: "Incomplete result: the final answer is still placeholder text (e.g. “Preparing the answer…”)",
      missingAnswer: "Incomplete Result: No User-Visible Final Answer",
      inconsistent: "Consistency Score {percent}%, Below This Run's Threshold",
    },
    attention: {
      failed: "Questions Below Aggregate Threshold",
      incomplete: "Incomplete Results",
      consistency: "Consistency {percent}%",
      consistencyPending: "Consistency Pending Review",
      skipped: "Skipped Questions",
    },
    promptSource: {
      unknown: "Unknown Source",
      run_override: "Run-Level Override",
      project_prompt: "Project Prompt Library",
      profile_recommended: "Evaluation Profile Recommendation",
      agent_selected: "AI Engineer Selection",
      agent_default: "AI Engineer Default",
      env_prompt_store: "Environment Prompt Library",
      system_default: "System Default",
    },
    table: {
      noResults: "This run has no result details yet.",
      noFilteredResults: "No results match the current filters.",
      searchLabel: "Search Results",
      filtersAria: "Result Filter Toolbar",
      searchPlaceholder: "Question ID Or Question",
      statusLabel: "Result Status",
      count: "{shown} / {total} rows",
      defaultSort: "Default sort: Question ID ascending · repeats within a question",
      clearFilters: "Clear Filters",
      runIndex: "Repeat {index}",
      completion: "{done}/{total} completed ({percent})",
      passed: "Passed",
      openDetail: "View Detail",
      openDetailAria: "View Detail: Question {caseId} Repeat {index}",
      retryCase: "Retry This Question",
      retrying: "Retrying",
      retriedTimes: "Retried {count} Times",
      turnLogs: "Turn Logs",
      paginationAria: "Result Pagination",
      range: "Rows {from}-{to} Of {total}",
      status: {
        attention: "Needs Attention",
        failed: "Failed",
        incomplete: "Incomplete Result",
        inconsistent: "Low Consistency",
        passed: "Passed",
      },
      column: {
        run: "Repeat",
        question: "Question",
        executedAverage: "Executed Turn Average",
        completion: "Completion",
        result: "Result",
        duration: "Duration",
        actions: "Actions",
      },
    },
    dialog: {
      title: "Question {caseId} · Repeat {index}",
      description: "Executed Turn Average {average} · {done}/{total} Turns Completed · {result}",
      reportTitle: "Question Details · {question}", reportDescription: "Shows each answer together with its Judge conclusion, latency, tokens, and runtime evidence.", repeatAnswersAria: "Repeated-Answer Selection", repeatAnswers: "View Answer", platformStability: "Platform Stability", answerStability: "Answer Stability", collectionTime: "Collection Time", completeAnswerTime: "Complete Answer Time", tokenUsage: "Token Usage", repeatAverageScore: "Question Quality Score", qualityDimensions: "Seven Quality Dimensions", successCount: "{completed} / {total} successful", questionAndEvidence: "Question And Scoring Evidence", questionAndGroundTruth: "Question And Ground Truth", question: "Question", groundTruth: "Ground Truth", answerContent: "Answer", answerCardAria: "Answer {index} Details", judgeConclusion: "Judge Conclusion", completeRepeatAnswers: "Repeated Answer Details", bestScoreSuffix: " · Highest In Report", qualityScoreShort: "Question Quality", fullTimeShort: "Complete", firstResponseShort: "First Response", toolTimeShort: "Tools", openTargetSession: "Open Target Session", openTargetSessionAria: "Open the target session for answer {index} in a new window", citationEvidence: "Citation Evidence", evidenceItem: "Evidence Item", runtimeSnapshotAndPrompt: "Report Runtime Versions And Prompt", report: "Report", platformAgentVersion: "Platform / AI Engineer Versions", targetPromptVersion: "Evaluated-Platform Prompt Version", repeatConfig: "Repeat Configuration", repeatConfigValue: "{count} Times / Question", targetPromptSnapshot: "Evaluated-Platform Prompt Snapshot",
      closeLabel: "Close Question Detail",
      turnLabel: "Turn {index}",
      previousCase: "Previous",
      previousCaseAria: "Previous Question",
      nextCase: "Next",
      nextCaseAria: "Next Question",
      queuePosition: "Item {position} Of {total} In Queue",
      openQuestion: "Open Question",
      openSuite: "Open Suite",
      openRun: "Open Source Run",
      tabsAria: "Question Detail Sections",
      artifactCard: "Data Card",
      artifactPaginationAria: "Data Card Pagination",
      artifactPage: "Page {page} / {pageCount} · Showing {rows} Rows",
      rowCount: "{count} rows",
      columnSummary: "Columns: {summary}",
      columnJoin: ", ",
      columnCount: " ({count} columns)",
      moreColumns: " And {count} Columns In Total",
      noDeclaredColumns: "No Declared Columns",
      noVisibleData: "No Visible Data",
      eventsLoadFailed: "Failed To Load Events",
      eventsLoading: "Loading events…",
      noEvents: "No event records yet.",
      eventDiscarded: "Discarded",
      eventTruncated: "Truncated",
      showFailedAttempts: "Show Failed Attempts",
      eventSummaryAria: "Event Summary",
      missingRunId: "Missing run identifier, cannot load events.",
      toolTrace: "Tool Trace ({count} Items, Excluded From Final Answer Scoring)",
      selectTurn: "View Turn",
      standardAnswer: "Reference Answer",
      agentAnswer: "AI Engineer Answer",
      repeatDiffAria: "Repeat Answer Diff",
      repeatDiffTitle: "Repeat Answer Comparison",
      currentSuffix: " (current)",
      scorePoints: "{score} pts",
      notScored: "Not Scored",
      blockedByPrevious: "Blocked By Previous Turn",
      blockedByPreviousReason: "The previous turn did not reach the pass threshold, so this turn was not executed.",
      evidenceIncomplete: "Incomplete Evidence",
      userInputAndAnswer: "User Input / Reference Answer",
      visibleFinalAnswer: "User-Visible Final Answer",
      retryHistory: "Technical Retry History",
      retryHistoryItem: "Repeat {index} · {status} · Score {score}",
      retryFixed: "Fixed",
      retryStillIncomplete: "Still Incomplete",
      incompleteReason: "Incompletion Reason",
      issues: "Issue Details",
      noIssues: "No issue details yet.",
      noIssuesWorkbench: "No Judge shortcomings, completeness or consistency anomalies.",
      nartAlignment: "NART Alignment",
      nartDimension: "Dimension",
      nartResult: "Result",
      tab: {
        answers: "Answer Comparison",
        answersWithCount: "Answer Comparison ({count} Repeats)",
        turns: "Turn Evidence ({count})",
        artifacts: "Data Cards ({count})",
        events: "Event Trace",
        issues: "Issue Details ({count})",
      },
    },
  },
  productionTags: {
    groupAria: "Question Tags",
    optionsAria: "{label} options",
    selectedAria: "Selected Tags",
    search: "Search Tags",
    removeAria: "Remove {label}",
    noneSelected: "No Tag Selected Yet",
    noMatch: "No Matching Tag",
    disabledSuffix: " (disabled)",
  },
  suites: {
    upload: {
      onlyXlsx: "Only .xlsx Excel suites are supported",
      selectProject: "Select The Project This Suite Belongs To",
      uploading: "Uploading The Excel File",
      parsed: "Parsing finished — confirm to write into the question bank and create the suite",
      parsedWithConflicts: "Parsing finished with {count} conflict(s); review them in the question bank",
      failed: "Suite Import Failed",
    },
    import: {
      panelAria: "Import Suite",
      fileInputAria: "Choose An Excel Suite File",
      selectProjectFirst: "Select The Owning Project First",
      chooseFile: "Choose Excel File",
      start: "Start Import",
      parsing: "Parsing…{elapsed}",
      parsingElapsed: "Parsing The File, Elapsed {elapsed}",
      parsedNote: "Parsing finished — review conflicts in the project question bank and confirm the write.",
      readyNote: "After parsing you will review question-bank conflicts; the suite is created only after you confirm.",
      downloadTemplate: "Download The Standard Excel Template",
      templateAfterProject: "Select A Project To Download The Template",
      guideTemplateTitle: "Standard Template: ",
      guideTemplateBody: "contains the “Questions”, “Turns”, “Available tags” and “Instructions” sheets, supporting both single-turn and multi-turn questions.",
      guideRequiredTitle: "Required Fields: ",
      guideRequiredBody: "the Questions sheet needs a Question ID; the Turns sheet needs Question ID, turn order, user input and ground-truth answer. One row per question means single-turn, two or more rows means multi-turn.",
      guideNote: "Tags are only filled in the Questions sheet. Only .xlsx files up to 50 MB are supported; after parsing you review question-bank conflicts, then the questions are written and the suite is created.",
    },
    caseEditor: {
      createTitle: "Add Question",
      editTitle: "Edit Question",
      description: "Maintain the question, ground-truth answer and tags of a single-turn question. Score thresholds are set when creating an evaluation run. Press Ctrl/⌘ + Enter to save.",
      draftUnsaved: "Draft Unsaved",
      save: "Save Question",
      saving: "Saving",
      requiredError: "Question ID And User Question Cannot Be Empty",
      caseIdLabel: "Question ID *",
      caseIdHelp: "Unique within the suite; cannot be changed after creation.",
      caseIdRequired: "Question ID is required.",
      questionLabel: "User Question *",
      questionHelp: "The input sent to the system under test.",
      questionRequired: "User question is required.",
      answerLabel: "Ground-Truth Answer",
      answerHelp: "Provided to the Judge as reference facts. Scoring still runs when empty, but only against the question and the current Judge prompt or default rules — filling it in reduces ambiguity.",
      tagsLabel: "Tags",
      tagsPlaceholder: "Separate Multiple Tags With Commas",
      enableCase: "Enable This Question",
      discardTitle: "Discard The Unsaved Question Draft?",
      discardDescription: "Closing the editor loses the question, ground-truth answer and tags you just entered.",
      discardConfirm: "Discard Draft",
      discardCancel: "Keep Editing",
    },
  },
  settings: {
    discovery: {
      subjectTenants: "Fetch Tenants",
      subjectAgents: "Fetch AI Engineers",
      offline: "Cannot reach the service, failed to {subject}; confirm the local backend is still running",
      forbidden: "Sign-in failed, or this account is not allowed to {subject}",
      failed: "Failed To {subject}",
    },
    field: {
      agentFrontendUrl: "AI Engineer Session Page URL",
      platformLoginUrl: "Platform Sign-In Page URL",
      loginUrl: "Sign-In URL",
      agentTimeout: "AI Engineer Timeout",
      agentConcurrency: "AI Engineer Concurrency",
      agentRetry: "AI Engineer Failure Retries",
      judgeConcurrency: "Judge Concurrency",
      judgeRetry: "Judge Failure Retries",
      repeatRuns: "Default Repeat Runs",
      repeatConcurrency: "Per-Question Repeat Concurrency",
      maxScore: "Maximum Score",
    },
    validation: {
      httpUrl: "{label} must be a full URL starting with http:// or https://",
      integerAtLeast: "{label} Must Be An Integer Of At Least {minimum}",
      integerBetween: "{label} Must Be An Integer Between {minimum} And {maximum}",
      judgeModelRequired: "Judge Model Cannot Be Empty",
      tenantRequired: "Select The Default Tenant For This Environment",
      repeatRunsMax: "Default Repeat Runs Cannot Exceed 50",
      repeatModeInvalid: "Repeat Execution Mode Is Invalid",
      passThresholdRange: "Pass Threshold Must Be Between 0 And 100",
      consistencyRange: "Consistency Threshold Must Be Between 0 And 100",
      passThresholdAboveMax: "Pass Threshold Cannot Exceed The Maximum Score",
      scoringRuleRequired: "Select A Scoring Rule",
      platformLoginUrlRequired: "Enter The Full Platform Sign-In Page URL",
      loginUsernameRequired: "Enter The Sign-In Username",
      loginPasswordRequired: "Enter The Sign-In Password",
      environmentNamePattern: "Environment ID accepts 1–50 letters, digits, underscores or hyphens, and must start with a letter or digit",
      environmentNameTaken: "Environment “{name}” Already Exists",
    },
  },
  agentEditor: {
    eyebrow: "AI Engineer Configuration",
    title: "AI Engineer Connections For This Environment",
    add: "Add AI Engineer",
    empty: "This environment has no AI Engineer yet. Add one first.",
    promptCustom: "Custom",
    promptInherit: "Inherit Environment",
    setDefaultAria: "Set {key} As The Default AI Engineer",
    createTitle: "Add AI Engineer",
    editTitle: "Edit AI Engineer",
    description: "You still need to submit the environment settings for changes to take effect.",
    dirtyDescription: "Draft not saved; you still need to submit the environment settings for changes to take effect.",
    save: "Save AI Engineer",
    noChanges: "No Changes",
    duplicateCode: "AI Engineer code “{code}” already exists. Use a different code.",
    discardTitle: "Discard The Unsaved AI Engineer Draft?",
    discardDescription: "Closing the editor discards the name, ID, code and prompt you just entered. Saved AI Engineers are unaffected.",
    discardConfirm: "Discard Draft",
    removeTitle: "Remove AI Engineer “{key}” From The Draft?",
    removeTitleFallback: "Remove AI Engineer?",
    removeDetails: "This only changes the current draft; it is written to the environment after you click “Save settings”, and reloading the page before that undoes it.",
    removeConfirm: "Remove From Draft",
    removeImpactNone: "The current default AI Engineer is unaffected.",
    removeImpactSwitch: "It is the current default AI Engineer; after removal the default switches to “{next}”.",
    removeImpactLast: "It is the only AI Engineer and the default one; after removal no new evaluation can be created until an AI Engineer is added again.",
    column: {
      name: "Name",
      id: "AI Engineer ID",
      agentCode: "AI Engineer Code",
      engineerCode: "Engineer Code",
      judgePrompt: "Judge Prompt",
      default: "Default",
      actions: "Actions",
    },
    field: {
      name: "AI Engineer Name",
      id: "AI Engineer ID *",
      agentCode: "AI Engineer Code *",
      engineerCode: "Engineer Code",
      judgePrompt: "AI Engineer-specific Judge prompt (leave empty to inherit the environment default)",
    },
  },
  connectionSetup: {
    stepperAria: "AI Engineer Connection Steps",
    step1: "Connect Account",
    step2: "Choose Tenant Scope",
    urlInvalid: "Enter a full address starting with http:// or https://.",
    credentialsTitle: "1. Connect The Platform Account",
    credentialsDescription: "Accessible tenants are read automatically after the account is verified; credentials never appear in discovery results.",
    verified: "Verified",
    saved: "Saved",
    pendingConnect: "Not Connected",
    platformLoginHelp: "Enter the browser login page URL; the token URL and AI Engineer API address are filled in automatically after connecting.",
    loginUsername: "Login Username",
    loginPassword: "Login Password",
    passwordSavedPlaceholder: "Saved; leave empty to keep using it",
    passwordPlaceholder: "Enter The Password",
    passwordHelp: "You must re-enter the password when changing the username.",
    usingSavedPassword: "The password saved for this environment will be used",
    ready: "Ready",
    fetchTenants: "Connect And Fetch Tenants",
    fetchingTenants: "Fetching tenants…",
    tenantTitle: "2. Choose The Default Tenant",
    tenantDescription: "The environment stores the tenants the account can access; the default tenant is only preselected when creating a run.",
    selected: "Selected",
    pendingSelect: "Not Selected",
    searchTenants: "Search Tenants",
    searchTenantsPlaceholder: "Search By Name, Key, ID Or Role",
    availableTenants: "Available Tenants",
    noTenantMatch: "No Matching Tenant",
    tenantSummary: "Default tenant: {name}; {count} accessible tenants stored",
    selectTenantHint: "Choose A Default Tenant Before Saving The Environment",
    savedTenant: "Saved tenant: {name}. Reconnect to view or switch tenants.",
    connectFirst: "Connect the account first and tenants will appear here.",
    agentUnbound: "AI Engineers are no longer bound to an environment. When creating an evaluation the system loads AI Engineers for the selected tenant, and multiple AI Engineers can be selected at once.",
    errorHint: "{message}. Your input and completed selections are kept, so you can retry directly.",
    readyTitle: "Environment Connection Is Ready",
    defaultTenant: "Default Tenant: {name}",
    tenantRecorded: "{count} accessible tenants recorded; AI Engineers are chosen when creating a run",
    saveConnection: "Save Environment Connection",
    advanced: "Advanced Settings And Manual Setup",
    advancedHint: "Use only when the platform does not support auto discovery or you need to override the default addresses. You still need to click save.",
    tenantKey: "Tenant Key",
    tenantId: "Tenant ID",
    agentBaseUrlHelp: "Root address of the target AI Engineer API gateway.",
    agentBaseUrlLabel: "AI Engineer API Base URL",
    tokenUrlLabel: "Token URL",
    agentTimeoutSeconds: "AI Engineer Timeout (Seconds)",
    agentTimeoutHelp: "Maximum wait for a single AI Engineer call; timeouts count as failures in the run result.",
    tokenUrlHelp: "Endpoint that issues the access token at runtime, not the browser login page.",
    agentFrontendUrlHelp: "Used to link back to the original AI Engineer session in reports; leave empty to skip the link.",
  },
  promptSettings: {
    loading: "Loading prompt configuration…",
    loadFailed: "Failed To Load Prompt Configuration: {message}",
    capsuleEyebrow: "Capsule Configuration",
    capsuleTitle: "Prompt Capsules",
    configNotApplied: "Configuration Not Applied",
    cancelConfigChanges: "Cancel Configuration Changes",
    saveConfig: "Save Capsule Configuration",
    configSaved: "Capsule Configuration Saved And Applied",
    configSaveFailed: "Failed To Save Capsule Configuration: {message}",
    configErrorHint: "{message}. Your changes are kept; retry or cancel.",
    enableCapsule: "Enable {name}",
    editorEyebrow: "Editor",
    editorFallbackTitle: "Prompt Editor",
    activeVersion: "Active Version",
    editingNewDraft: "Editing A New Version Draft",
    selectPrompt: "Select A Prompt",
    versionHelp: "Save the capsule configuration after switching versions for it to take effect.",
    name: "Name",
    description: "Description",
    body: "Prompt Body",
    insertVariable: "Click To Insert A Variable: ",
    undeclaredVariables: "Undeclared variables: {variables}. Remove them or use the variables above.",
    undeclaredVariablesToast: "Undeclared Variables Found: {variables}",
    fixVariablesFirst: "Resolve The Undeclared Variables First",
    restoreDefault: "Restore And Save Default",
    cancelDraft: "Cancel Draft",
    createPrompt: "Create Prompt",
    savePrompt: "Save Prompt",
    promptCreated: "Prompt created; save the capsule configuration to apply it",
    promptSaved: "Prompt Saved",
    promptDeleted: "Prompt Deleted",
    promptRestored: "Default Prompt Restored",
    copyName: "{name} copy",
    newVersionName: "{name} New Version",
    copyAnnouncement: "A prompt copy draft was created. Confirm its name first.",
    newVersionAnnouncement: "A new prompt version draft was created. Enter its name first.",
    discardTitle: "Discard The Unsaved Prompt Draft?",
    discardUrlChange: "The address bar points to another capsule or version. Switching discards the unsaved name, description and body in the editor; saved versions are unaffected.",
    discardSwitchCapsule: "Switching capsules discards the unsaved name, description and body in the editor; saved versions are unaffected.",
    discardSwitchVersion: "Switching versions discards the unsaved name, description and body in the editor; saved versions are unaffected.",
    discardCreate: "Creating a version discards the unsaved name, description and body in the editor; saved versions are unaffected.",
    keepDraft: "Stay On The Draft",
    discardAndContinue: "Discard Draft And Continue",
    currentCapsule: "The Current Capsule",
    restoreTitle: "Restore The System Default Prompt For “{name}”?",
    restoreDescription: "Restoring writes the system default prompt immediately and overrides the capsule's current selection.",
    restoreDescriptionDirty: "Restoring writes the system default prompt immediately, overrides the capsule's current selection and discards the unsaved draft in the editor.",
    restoreDetails: "This takes effect immediately and cannot be undone; historical runs keep the prompt snapshot they used.",
    restoring: "Restoring…",
    deleteTitle: "Delete Prompt “{name}”?",
    deleteDescription: "After deletion the version disappears from the version list; if it is the active version the capsule falls back to another one.",
    deleteDetails: "This cannot be undone; finished runs keep the prompt snapshot they used.",
    deletePrompt: "Delete Prompt",
    deleting: "Deleting…",
  },
  security: {
    managedByPlatformEyebrow: "Unified Identity",
    managedByPlatformTitle: "Account Managed By IM Production",
    managedByPlatformDescription: "Unified sign-in is active. Manage your avatar, password and account profile in IM Production; Agent Judge does not store a separate password.",
    platformAccount: "Platform Account",
    platformTenant: "Tenant",
    accountProfile: "Account Profile",
    adminAvatar: "Administrator Avatar",
    currentAvatarAlt: "Current Avatar",
    chooseAvatar: "Choose An Avatar Image",
    avatarSaving: "Saving avatar…",
    removeAvatar: "Remove Avatar",
    avatarHelp: "PNG, JPG, WebP and GIF up to 10 MB; the image is center-cropped and saved immediately.",
    avatarNotImage: "Select An Image File",
    avatarTooLarge: "The Image Must Be 10 MB Or Smaller",
    avatarProcessFailed: "Failed To Process The Avatar",
    avatarProcessUnsupported: "This Browser Cannot Process The Avatar Image",
    avatarUpdated: "Avatar Updated",
    removeAvatarTitle: "Remove The Current Administrator Avatar?",
    removeAvatarDescription: "Confirming writes to the server immediately and clears the stored avatar image; the avatar falls back to the username initial. This cannot be undone and requires uploading a new image to restore.",
    removeAvatarDetails: "The account password and login sessions are unaffected.",
    keepAvatar: "Keep Avatar",
    confirmRemoveAvatar: "Remove Avatar",
    removingAvatar: "Removing…",
    passwordEyebrow: "Password",
    changePassword: "Change Password",
    changing: "Changing…",
    currentUsernameAria: "Current Account Username",
    currentPassword: "Current Password",
    newPassword: "New Password (At Least 8 Characters)",
    confirmPassword: "Confirm New Password",
    strength: "Strength: {level}",
    strengthEmpty: "Not Entered",
    strengthShort: "Too Short",
    strengthStrong: "Strong",
    strengthFair: "Fair",
    passwordAdvice: "Use 12 characters or more and mix upper and lower case letters, digits and symbols.",
    passwordMismatch: "The Two New Passwords Do Not Match",
    passwordSameAsCurrent: "The new password must differ from the current one",
    passwordChanged: "Password changed. All sessions were signed out; sign in again with the new password",
    currentPasswordWrong: "The current password is incorrect. Try again",
  },
  tags: {
    legend: "Tags",
    actionFailed: "The action failed. Try again later",
    loadingTags: "Loading Tags",
    facetLoadFailed: "Failed To Load Tag Filters",
    facetUnavailable: "The Current Tag Filter Is No Longer Available",
    noTags: "No tags have been assigned to these questions yet",
    clearFilter: "Clear Tag Filter",
    searchFacetAria: "Search Filter Tags",
    searchPlaceholder: "Search Tags",
    searchAvailableAria: "Search Available Tags",
    searchTagOrDimension: "Search Tags Or Dimensions",
    groupFilterAria: "{name} filter",
    questionCount: "{count} questions",
    collapse: "Collapse",
    expandAll: "Show All ({count})",
    noMatch: "No Matching Tag",
    selectedAria: "Selected Tags",
    noneSelected: "No Tag Selected Yet",
    disabled: "Disabled",
    disabledSuffix: " (disabled)",
    removeAria: "Remove {label}",
    creating: "Creating…",
    createAndSelect: "Create Project Tag “{label}” And Select It",
    sameNameDisabled: "A tag with the same name is disabled. Re-enable it in tag management first.",
    sameNameExists: "A tag with the same name already exists; select it directly.",
    reusedExisting: "Reused An Existing Tag",
    created: "Project Tag Created",
    renamed: "Tag renamed; the tag ID is unchanged",
    enabledToast: "Tag Enabled",
    disabledToast: "Tag disabled; existing references are unchanged",
    deleted: "Unused Tag Deleted",
    managerTitle: "Project Tag Management",
    managerDescription: "Maintain the tags available in this project; workspace standard tags can only be enabled or disabled here.",
    managerClose: "Close Tag Management",
    scopeFilterAria: "Filter By Tag Scope",
    scopeAll: "All Scopes",
    scopeProject: "Project Tags",
    scopeWorkspace: "Workspace Standard",
    scopeProjectShort: "Project",
    scopeWorkspaceShort: "Workspace",
    statusFilterAria: "Filter By Tag Status",
    statusAll: "All Statuses",
    statusActive: "Enabled",
    statusDisabled: "Disabled",
    newProjectTag: "New Project Tag",
    newTagPlaceholder: "e.g. Inventory focus",
    readOnlyNote: "This project is read-only; tags can only be viewed.",
    loadingProjectTags: "Loading Project Tags",
    loadProjectTagsFailed: "Failed To Load Project Tags",
    rename: "Rename",
    renameAria: "Rename {label}",
    disable: "Disable",
    reEnable: "Re-enable",
    noMatchingTags: "No Tag Matches The Filters",
    noProjectTags: "No Project Tag Yet",
    noVisibleTags: "This project has no tags to view.",
    createFirstTag: "You can create the first project tag.",
    renameTitle: "Rename Tag",
    renameDescription: "Only the display name changes; historical tag IDs are kept.",
    saveName: "Save Name",
    maxLengthHint: "Up to 80 characters.",
    labelRequired: "Tag name is required.",
    deleteTitle: "Delete This Tag?",
    deleteDescription: "No question version references this tag; deletion cannot be undone.",
    deleteConfirm: "Delete Tag",
    disableTitle: "Disable This Tag?",
    disableDescription: "Historical question versions still reference this tag. After disabling it cannot be assigned, but existing content is kept.",
    disableDescriptionUsed: "{count} active questions reference this tag. After disabling it cannot be assigned, but existing content is kept.",
    disableConfirm: "Disable Tag",
    discardTitle: "Discard The Uncreated Tag Name?",
    discardDescription: "“{label}” has not been created and will be lost when tag management closes.",
    discardConfirm: "Discard And Close",
    column: {
      label: "Tag Name",
      dimension: "Dimension",
      scope: "Scope",
      questions: "Questions",
      status: "Status",
    },
  },
  settingsPage: {
    title: "Settings",
    description: "Only account security plus output and storage live here. Run parameters, scoring rules and prompts are maintained inside each project.",
    unsaved: "Unsaved Changes",
    navAria: "Settings Sections",
    migratedSection: "Configuration Moved",
    securityGuard: "The password form has not been submitted. Leaving clears the current and new password you entered, and you will need to fill them in again.",
    backToRunDetail: "Back To Run Detail",
    backToNewRun: "Back To New Run",
    backToReports: "Back To Reports",
    backToLogs: "Back To Logs",
    backToQueue: "Back To Run Queue",
    bannerTitle: "Project-Level Evaluation Settings Have Moved",
    bannerDescription: "Run parameters, scoring rules and prompts moved to the project evaluation profiles and prompt library; they are no longer maintained in the legacy environment settings.",
    migratedTitle: "Run Parameters / Scoring Rules / Prompts Have Moved",
    migratedDescription: "These settings now belong to each project's evaluation profiles and prompt library. Open the project from the projects page to maintain them; the legacy environment switch is gone.",
    goToProjects: "Go To Projects",
    offline: "Cannot reach the service. Check the network or confirm the local backend is still running",
    checkFailedStale: "This check failed; the data below was fetched successfully at {time}. {message}",
    checkFailed: "Cannot read storage status. {message}",
    rechecking: "Rechecking; showing data fetched at {time}.",
    checkedAt: "{provider} · Read-Only Check Completed At {time}",
    loadingStatus: "Reading Platform Status",
    checkingStorage: "Checking Storage Connection",
    storageUnavailable: "Storage Status Unavailable",
    recheckingStorage: "Rechecking Storage Connection",
    storageOk: "Storage Connection Is Healthy",
    checking: "Checking…",
    recheck: "Check Again",
    awaitingCheck: "Awaiting Check",
    lastSuccessfulCheck: "Last successful check: {time}. ",
    storageService: "Storage Service",
    storageServiceNote: "Environment settings, suites and run records share the same remote storage.",
    currentData: "Current Data",
    currentDataValue: "{suites} Suites · {runs} Runs",
    currentDataNote: "Counts come from a read-only query against the current database.",
    executor: "Run Executor",
    executorWorkers: "PostgreSQL Queue · {count} Workers Online",
    executorInline: "Web Process Threads (Compatibility Mode)",
    executorQueue: "{queued} queued, {active} running; runs support lease/heartbeat recovery.",
    executorHint: "You can switch to the postgres_worker production mode at deploy time.",
    export: "Result Export",
    exportFormats: "JSON, Excel",
    exportNote: "Supports full results and the failed-question scope.",
    logQuery: "Log Query",
    logQueryValue: "In-Page Filters, JSONL Export",
    logQueryNote: "Filter by run, question, repeat index, type and date.",
    viewResults: "View Run Results",
    viewLogs: "Query Logs",
    section: {
      security: "Account Security",
      output: "Output And Storage",
    },
  },
  projectRuns: {
    title: "{name} · Runs",
    description: "Track evaluation runs for this project; use “New run” to start one.",
    openQueue: "Open Run Queue",
    loadingTitle: "Loading Project Runs",
    loadingDescription: "Reading the run status for this project.",
    errorTitle: "Failed To Load Project Runs",
    emptyTitle: "No Project Run Yet",
    emptyDescription: "Run records appear here once you create an evaluation with this project's suites.",
    tableLabel: "Project Runs",
    column: {
      run: "Run",
      status: "Execution Status",
    },
  },
  projectSuites: {
    title: "{name} · Suites",
    description: "Suites here are ready to run; the question bank holds the raw material you compose them from.",
    questionBank: "Question Bank",
    composeFromBank: "Create From Question Bank",
    loadingTitle: "Loading Project Suites",
    loadingDescription: "Reading test assets that belong to this project.",
    errorTitle: "Failed To Load Suites",
    emptyTitle: "No Project Suite Yet",
    emptyDescription: "Maintain questions in the question bank first, then create a suite from the selected questions.",
    emptyAction: "Go To The Question Bank",
    tableLabel: "Project Suites",
    createRun: "Create Run",
    tableMeta: "{count} Suites · Sorted By {sort}",
    searchAria: "Search Test Suites",
    searchPlaceholder: "Search Suite Name Or ID",
    typeFilterAria: "Filter By Suite Type",
    allTypes: "All Types",
    statusFilterAria: "Filter By Suite Status",
    noMatchTitle: "No Matching Test Suites",
    noMatchDescription: "Adjust the search term, type, or status filter.",
    column: {
      cases: "Questions",
    },
  },
  projectSettings: {
    title: "{name} · Settings",
    description: "Use the readiness checklist to confirm everything needed to start a run; once complete you can start from “Runs”.",
    dirty: "Unsaved Changes",
    guard: "Project defaults have unsaved changes that will be lost if you leave.",
    saved: "Project Defaults Saved",
    saveFailed: "Save Failed: {message}",
    refreshed: "Settings Data Refreshed",
    loadingTitle: "Loading Project Settings",
    loadingDescription: "Reading the default evaluation profile and readiness state.",
    openFailed: "Cannot Open This Project",
    backToProjects: "Back To Projects",
    lastSync: "Last Sync {time}",
    readinessTitle: "Run Readiness Checklist",
    readinessSummary: "{ready}/{total} ready. When a precheck fails you can deep-link from the new run page to the gap.",
    configure: "Configure",
    defaultsTitle: "Project Defaults",
    defaultsDescription: "Owner and default evaluation profile follow the saved API values; nothing is pre-selected when unset.",
    cancelChanges: "Cancel Changes",
    owner: "Owner",
    ownerAria: "Project Owner",
    ownerPlaceholder: "Name Or Email",
    defaultProfile: "Default Evaluation Profile",
    noProfiles: "No Profile Yet",
    notSet: "Not Set",
    profileSetHint: "After saving, the overview and new run page preselect this profile.",
    profileUnsetHint: "Currently unset: the readiness checklist stays amber and new runs need a manual choice or the fallback profile.",
    createProfileHintPrefix: "Create One In ",
    createProfileHintSuffix: " first.",
    profilesUnavailable: "The Evaluation Profile API Is Unavailable: {message}",
    agentContext: "AI Engineer Context",
    agentContextNone: "No Additional Context",
    agentContextContract: "IM Production Page URL",
    agentContextHint: "Optionally add an IM Production page URL. Agent Judge sends only the current route, including query parameters; IM Production resolves the business context.",
  },
  projects: {
    description: "A project is the single ownership boundary for questions, suites and run records.",
    create: "New Project",
    createDescription: "After creation you can maintain its own question bank, suites and run records.",
    createSubmit: "Create Project",
    createFirst: "Create The First Project",
    closeCreateForm: "Close The New Project Form",
    created: "Project “{name}” Created",
    createFailed: "Creation Failed: {message}",
    createFailedTitle: "Failed To Create The Project",
    checkInput: "Check Your Input",
    search: "Search Projects",
    searchPlaceholder: "Project Name, Key Or Owner",
    loadingTitle: "Loading Projects",
    loadingDescription: "Fetching projects and their question bank status.",
    errorTitle: "Failed To Load Projects",
    errorDescription: "Projects are temporarily unavailable.",
    emptyTitle: "No Project Yet",
    emptyDescription: "Create a project to start adding questions and organising suites.",
    noMatchTitle: "No Matching Project",
    noMatchDescription: "Adjust the search term or status filter.",
    tableLabel: "Project List",
    unassigned: "Unassigned",
    open: "Open Project",
    status: {
      active: "Active",
      draft: "Draft",
      archived: "Archived",
    },
    field: {
      name: "Project Name",
      key: "Unique Key",
      type: "Project Type",
      defaultEnvironment: "Default Environment",
      description: "Business Description",
    },
    column: {
      questions: "Questions",
      lastRun: "Last Run",
    },
  },
  overview: {
    title: "Overview Console",
    description: "Check projects, question bank assets and the latest evaluation runs.",
    loadingTitle: "Loading Workspace",
    loadingDescription: "Aggregating projects, suites and run records.",
    errorTitle: "Failed To Load The Overview",
    errorDescription: "Data is temporarily unavailable.",
    storageErrorTitle: "Storage Connection Problem",
    storageErrorDetail: "Current storage: {provider}. Restore the connection before creating a run.",
    checkSettings: "Check System Settings",
    failedRunsTitle: "{count} Failed Runs Need Attention",
    failedRunsDetail: "Latest Failure: {name}",
    viewFailedRuns: "View Failed Runs",
    noProjectsTitle: "No Active Project Yet",
    noProjectsDetail: "A project owns questions and suites; create one before preparing suites.",
    noSuitesTitle: "No Suite Yet, Cannot Start A Run",
    noSuitesDetail: "Evaluations take a suite as input; compose one in the project suites first.",
    prepareSuites: "Prepare Suites",
    activeRunsTitle: "{count} Runs Are Executing",
    activeRunsDetail: "Open the run queue to track progress, stop or resume.",
    trackActiveRuns: "Track Running Jobs",
    readyTitle: "Assets Are Ready, You Can Create A Run",
    readyDetail: "Projects and suites are in place; pick a project to start.",
    statusStripAria: "Platform Status And Next Step",
    failedRunsCount: "Failed Runs: {count}",
    activeRunsCount: "Running: {count}",
    storageState: "Storage: {state}",
    storageOk: "Healthy · {provider}",
    storageError: "Error",
    connected: "Connected",
    quickLinksAria: "Quick Links",
    recentRuns: "Recent Runs",
    recentRunsHint: "Unified execution view of all evaluation runs (8 newest by creation time)",
    viewAll: "View All",
    noRunsTitle: "No Run Record Yet",
    noRunsDescription: "Pick a suite and create an evaluation first.",
    health: "Run Health",
    healthHint: "Storage And Run Status",
    running: "Running",
    runningHint: "Validating Or Executing",
    queued: "Queued",
    queuedHint: "Waiting For A Worker",
    storage: "Storage",
    statusChecking: "Checking Status",
    metric: {
      activeProjects: "Active Projects",
      activeProjectsHint: "Ownership Boundary For Questions And Test Assets",
      questions: "Project Questions",
      questionsHint: "Total Active Questions Across Projects (Archived Excluded)",
      suitesHint: "Uses Specific Question Versions",
      active: "Running",
      activeHint: "Queued, Validating, Running Or Finalising",
    },
    link: {
      projects: "Open a project and maintain its questions and suites",
      questionBank: "Pick A Project Question Bank To Maintain",
      suites: "Review Evaluation Assets And Their Selected Versions",
    },
    column: {
      time: "Time",
    },
  },
  failures: {
    title: "Failed Question Review",
    runMissing: "The run does not exist or has been deleted",
    requestFailed: "Request Failed",
    selectCase: "Select a failed question to see its detail.",
    caseHeading: "Question {caseId}",
    previousAria: "Previous Failed Question",
    nextAria: "Next Failed Question",
    viewInRun: "View In The Original Run",
    viewTurnLogs: "View Logs For This Turn",
    retryIncomplete: "Retry Incomplete Result",
    userQuestion: "User Question",
    belowThreshold: "Below the pass threshold; review the answer against the reference.",
    retryStarted: "Technical Retry Started For Question {caseId} Repeat {index}",
    retryFailed: "Retry Failed: {message}",
    downloadStarted: "Download Of Failure Details Started",
    downloadFailed: "Download Failed: {message}",
    downloading: "Downloading",
    downloadDetails: "Download Failure Details",
    loading: "Loading Failed Questions",
    loadFailed: "Failed To Load Failed Questions",
    retryConfirmTitle: "Retry Question “{caseId}” Repeat {index}?",
    retryConfirmDescription: "This repairs the incomplete result within the current run; the original failure record stays archived.",
    retryConfirm: "Retry",
    failedCount: "{count} Failed Repeated Answers",
    affectedCases: "{count} Questions Affected",
    runId: "Run ID: {id}",
    minScore: "Min Score",
    maxScore: "Max Score",
    correctness: "Correctness",
    incorrect: "Incorrect",
    correct: "Correct",
    filterSummary: "Showing {shown} / {total} failed repeated answers across {cases} questions. Sorted by Question ID ascending and by repeated-answer number within each question; the score range is only a secondary filter and does not decide failure.",
    tableLabel: "Failed Question List",
    rowAria: "View failed question {caseId}, repeat {index}, executed turn average {average}, {done}/{total} completed, failed",
    noFiltered: "No failed question matches the current filters.",
    paginationAria: "Failed Question Pagination",
  },
  logsPage: {
    networkFailed: "Network connection failed. Check that the service is reachable and retry.",
    loadFailed: "Failed To Load Logs",
    loading: "Loading Logs",
    backToFailures: "Back To Failure Review",
    backToRunList: "Back To The Run List",
    copied: "Log Copied",
    copyFailed: "Copy Failed: {message}",
    exportStarted: "Log Export Started",
    exportFailed: "Log Export Failed: {message}",
    exportFiltered: "Export Current Filter ({count} Entries) · JSONL",
    exportAll: "Export All Logs ({count} Entries) · JSONL",
    exportBlockedError: "Logs failed to load; reload before exporting",
    exportBlockedLoading: "Export Is Available Once Logs Finish Loading",
    exportBlockedEmpty: "No Log Matches The Current Filters",
    exporting: "Exporting",
    currentRun: "Current Run: ",
    selectRunHint: "Select A Run To Query Its Logs",
    confirmingRun: "Confirming The Run Record",
    confirmRunFailed: "Cannot Confirm The Run Record",
    reloadRuns: "Reload Run List",
    linkExpired: "This log link is no longer valid. Go back to the logs home and pick an existing run.",
    viewAvailableLogs: "View Available Logs",
    runsLoadFailed: "Failed To Load Runs",
    noLogsTitle: "No Run Log Yet",
    noLogsDescription: "AI Engineer and Judge logs appear here after an evaluation completes.",
    importSuite: "Import Suite",
    runSelect: "Run",
    noRuns: "No Run Available",
    caseIdPlaceholder: "e.g. QUESTION-0001",
    runIndex: "Repeat Index",
    dateFrom: "Start Date",
    dateTo: "End Date",
    query: "Query",
    querying: "Querying",
    filtersDirty: "Filters changed. Press Enter or click “Query” to apply.",
    content: "Log Content",
    contentPlaceholder: "Search Log Content",
    categoryGroup: "Log Type",
    rawLevel: "Raw Level: {label}",
    rawLevelLabel: "Raw Level",
    rawLevelTitle: "Raw Level Value {level}",
    clearRawLevel: "Clear Raw Level Filter",
    resetFilters: "Reset Filters",
    dateRangeInvalid: "The end date cannot be earlier than the start date.",
    rowAria: "View Log Detail: {time}, {category}, {caseId}, {message}",
    noCaseId: "No Question ID",
    noMessage: "No Content",
    noFiltered: "No log matches the current filters.",
    noLogsForRun: "This run has no log record yet.",
    countRange: "Entries {from}-{to} Of {total} Logs · Newest First, {pageSize} Per Page",
    countEmpty: "0 Logs · Newest First, {pageSize} Per Page",
    detailTitle: "Log Detail",
    detailEmpty: "Select a log entry to see its full content.",
    businessType: "Business Type",
    runIdLabel: "Run ID",
    redactNote: "Credential fields are redacted; “Copy log” copies the redacted content too.",
    copyLog: "Copy Log",
    viewRelatedCase: "View Related Question",
    openRunResult: "Open Run Results",
    category: {
      error: "Run Error",
      finding: "Judge Finding",
      warning: "Run Warning",
      info: "Run Info",
    },
    column: {
      source: "Source",
      message: "Message",
    },
  },
  projectDetail: {
    loadingTitle: "Loading Project",
    loadingDescription: "Fetching the project's questions, suites and runs.",
    missing: "The project does not exist or is temporarily unavailable.",
    noProfileTitle: "Configuration Gap: No Default Evaluation Profile Saved",
    noProfileDetail: "The overview and new run page follow the saved default profile; the API currently reports none.",
    saveDefaultProfile: "Save A Default Evaluation Profile",
    noBindingTitle: "No Environment Binding Configured",
    noBindingDetail: "Configure stage × endpoint × tenant before AI Engineers can be loaded for a run.",
    noBindingHint: "Configure the platform address, account and tenant per stage under “Environment bindings”.",
    noBindingReadOnly: "Read-only projects cannot add environment bindings.",
    configureFirstBinding: "Configure The First Binding",
    addBinding: "Add Environment Binding",
    addBindingShort: "Add Binding",
    readyWithReportTitle: "Project Is Runnable; A Recent Report Exists",
    latestReportDetail: "Latest Report: {name}",
    viewLatestReport: "View The Latest Report",
    readyTitle: "Project Is Ready, You Can Create A Run",
    readyDetail: "The default evaluation profile and environment binding are in place.",
    statusStripAria: "Project Status And Next Step",
    defaultProfileLabel: "Default Evaluation Profile: ",
    profileConfigured: "Configured · {name}",
    notConfigured: "Not Configured",
    latestReportLabel: "Latest Report: ",
    none: "None",
    basicInfo: "Project Basics",
    basicInfoHint: "Configuration truth and governance fields; duplicate entries are consolidated into the status strip and KPIs.",
    ownerHint: "Person Responsible For This Project",
    assignOwner: "Assign An Owner",
    defaultProfileHint: "Follows The Saved API Field",
    projectKey: "Project Key",
    projectKeyHint: "Stable Unique Identifier",
    bindingSummary: "Environment Binding Summary",
    bindingSummaryHint: "Only this summary stays on the first screen; manage and add bindings in settings.",
    manage: "Manage",
    bindingTableLabel: "Project Environment Bindings",
    metric: {
      questionsHint: "Enabled Questions (Matches The Question Bank “Enabled” Filter)",
      runs: "Run Records",
      runsHint: "Historical Evaluations For This Project",
      bindings: "Environment Bindings",
      bindingsHint: "Stage × Endpoint × Tenant",
    },
    link: {
      content: "Suites first; the question bank supplies the material",
      runs: "Track Run Progress And History",
      tasks: "Create Evaluations And Track Queue, Execution, And Failures",
      results: "Review Per-Question Output, Completeness, Scores, And Pass Status",
      reports: "Review Conclusions And Details Of Finished Evaluations",
      production: "Filter And Sync Real Conversations Into Ground Truth",
      questions: "Add, Edit, View History And Copy Across Projects",
      settings: "Environments, Evaluation Profiles, Prompts And Project Defaults",
    },
    column: {
      connection: "Connection",
      tenant: "Tenant",
      stage: "Stage",
    },
  },
  projectPrompts: {
    title: "{name} · Prompts",
    description: "Maintain answer scoring prompts for this project; they can be selected and tweaked when creating a run.",
    createTitle: "New Prompt",
    createSubmit: "Create Prompt",
    editTitle: "Edit “{name}”",
    editorDescription: "The body selected at run time is snapshotted into the run; this library holds reusable bodies, not the scoring rule itself.",
    unsaved: "Unsaved",
    clearDraft: "Clear Draft",
    namePlaceholder: "e.g. Strict scoring prompt",
    descriptionPlaceholder: "Use Question Or Maintenance Note (Optional)",
    body: "Body",
    bodyPlaceholder: "Write The Full Judge Prompt",
    guard: "The prompt has unsaved changes that will be lost if you leave.",
    created: "Prompt “{name}” Created",
    updated: "Prompt “{name}” Updated",
    updateFailed: "Update Failed: {message}",
    deleted: "Prompt Deleted",
    deleteFailed: "Delete Failed: {message}",
    refreshed: "Prompts Refreshed",
    loadingProject: "Fetching the prompt library.",
    loading: "Loading Prompts",
    loadingHint: "On first open, if the library is empty the backend generates a system default body.",
    unavailable: "The Prompt Library Is Unavailable",
    emptyTitle: "No Prompt Yet",
    emptyDescription: "Refresh to trigger the system default, or create one directly.",
    total: "{count} total",
    tableLabel: "Project Prompt Library",
    referencedByDefault: "Referenced By The Default Profile",
    notReferenced: "Not Referenced By Any Profile",
    noDescription: "No Description",
    readOnly: "Read-only",
    discardTitle: "Discard Unsaved Prompt Changes?",
    discardDescription: "Closing the editor discards the name, description and body you entered; saved prompts are unaffected.",
    discardConfirm: "Discard Changes",
    deleteTitle: "Delete Prompt “{name}”?",
    deleteTitleFallback: "Delete prompt?",
    deleteDescription: "Prompt snapshots in historical runs are not rewritten. If a profile still references it, that profile falls back to the system default.",
    deleteReferences: "Currently Referenced By: {names}",
    deleteConfirm: "Delete",
    column: {
      references: "References",
      preview: "Description / Body Preview",
      updated: "Updated",
    },
  },
  queue: {
    description: "Review evaluation status across projects and filter by project. For daily work, prefer the project-level Runs page.",
    projectDescription: "Create evaluation tasks and track queue, execution, failure, and retry states.",
    dataUnavailable: "Run Data Is Unavailable",
    refreshing: "Refreshing",
    totalRuns: "{count} Runs In Total",
    totalTasks: "{count} Evaluation Tasks",
    updatedSuffix: " · Updated {time}",
    searchPlaceholder: "Search Run Name, ID, AI Engineer Or Environment",
    searchAria: "Search Runs",
    searchTaskPlaceholder: "Search Task Name, ID, AI Engineer, Or Environment",
    searchTasksAria: "Search Evaluation Tasks",
    statusAll: "All Statuses",
    statusFilterAria: "Filter By Run Status",
    projectFilterAria: "Filter Runs By Project",
    allProjects: "All Projects",
    environmentFilterAria: "Filter Test Tasks By Environment",
    allEnvironments: "All Environments",
    agentFilterAria: "Filter Test Tasks By AI Engineer",
    filterSummary: "{shown} / {total} matched",
    loadingTitle: "Loading Run Queue",
    loadingDescription: "Fetching the latest run status, please wait.",
    errorTitle: "Failed To Load The Run Queue",
    errorDescription: "The run list is temporarily unavailable.",
    emptyTitle: "No Evaluation Runs Yet",
    emptyDescription: "After you create the first run, you can track its progress and open results here.",
    emptyProjectDescription: "This project has no evaluation tasks yet.",
    noMatchTitle: "No Matching Runs",
    noMatchDescription: "Adjust the search keyword or status filter.",
    tableLabel: "Run Queue",
    projectTableLabel: "Project Evaluation Tasks",
    batch: "Batch {position}/{size} · {id}",
    progressAria: "Run Progress {name}",
    questionCompletedSummary: "{completed}/{total} questions completed normally; {incomplete} incomplete",
    questionProcessedSummary: "{done}/{total} Questions Processed",
    questionCompletionUnknown: "{total} questions in total; completed count unavailable",
    viewDetail: "View Details",
    viewDetailAria: "View Details {name}",
    actionAria: "{action} {name}",
    stop: "Stop",
    stopping: "Stopping",
    resume: "Resume",
    resuming: "Resuming",
    newRunDescription: "Pick an environment, AI Engineer, suite and repeat count to start.",
    column: {
      run: "Test Task",
      environment: "Environment",
      agentContext: "AI Engineer Context",
      status: "Status",
      progress: "Progress",
      cases: "Completed Questions",
      repeat: "Repeat",
    },
    confirm: {
      deleteTitle: "Delete This Run?",
      deleteDescription: "Deleting removes the run results, reports and log records. This cannot be undone.",
      stopTitle: "Stop This Run?",
      stopDescription: "The running evaluation will be aborted. A stopped run cannot be resumed; to execute again, open the run details and choose Rerun.",
      resumeTitle: "Resume This Run?",
      resumeDescription: "Execution continues from the saved progress and may trigger new model calls.",
      target: "Run: {name}",
      stop: "Confirm Stop",
      resume: "Confirm Resume",
    },
  },
  connections: {
    title: "Connections",
    description: "Configure the target platform URL (for example https://tesaqaedev.tesa.cn/) and login account, then verify that tenants can be fetched. Judge models are configured in evaluation profiles.",
    create: "New Connection",
    createFirst: "Create The First Connection",
    scope: "Scope",
    scopeFilterAria: "Filter Connections By Scope",
    scopeWorkspace: "Workspace Shared",
    scopeProject: "Project Private",
    scopeProjectDefault: "Project Private (Default)",
    summary: "{count} In Total",
    summaryTotal: " / {total} overall",
    loadingTitle: "Loading Connections",
    loadingDescription: "Reading the connection list.",
    errorTitle: "Connection List Is Unavailable",
    emptyTitle: "No Connections Yet",
    emptyDescription: "Create a connection with the platform URL and account first, so projects can fetch tenants / AI Engineers and start evaluations.",
    noMatchTitle: "No Connections Match The Filter",
    noMatchDescription: "No connection under the current filter “{scope}”; {total} connections in total.",
    noPlatformUrl: "No Platform URL",
    passwordConfigured: "Password Saved",
    notConfigured: "Not Configured",
    fetchTenants: "Fetch Tenants",
    goToEnv: "Configure Environment",
    legacyMappingTitle: "Mapping From The Legacy Environment Settings",
    legacyMappingBody: "URL + login account → this Connections page; tenant / AI Engineer binding → project Environment; Judge model → evaluation profile.",
    createTitle: "New Connection",
    editTitle: "Edit Connection · {name}",
    createDescription: "Fill in the platform URL and account → verify with Connect and fetch tenants → save. Then pick tenant / AI Engineer in the project Environment page.",
    editDescription: "After updating the URL or account you can fetch tenants again to verify. Leave the password blank to keep it unchanged.",
    unsaved: "Unsaved",
    unsavedGuard: "The connection form has unsaved changes that will be lost if you leave.",
    revert: "Revert Changes",
    fetchingTenants: "Fetching tenants…",
    connectAndFetch: "Connect And Fetch Tenants",
    fetchWithSaved: "Fetch With Saved Credentials",
    saveConnection: "Save Connection",
    saveChanges: "Save Changes",
    noChanges: "No Changes",
    connectorType: "Connector Type",
    connectorTypeHelp: "Identifier of the target platform type; defaults to im_production.",
    owningProject: "Owning Project",
    noProjects: "No Available Project",
    platformUrl: "Platform URL / Login Page",
    platformUrlHelp: "Enter the site root or /login; the tenant login and AI Engineer API are derived automatically.",
    username: "Login Username",
    password: "Login Password",
    passwordSavedPlaceholder: "Saved; leave blank to keep",
    passwordPlaceholder: "Enter The Password",
    discardTitle: "Discard Unsaved Connection Changes?",
    discardDescription: "Closing the editor discards the name, platform URL, account and password you entered. Saved connections are unaffected.",
    keepEditing: "Keep Editing",
    discardConfirm: "Discard Changes",
    createdToast: "Connection “{name}” created. Fetch tenants / AI Engineers from the project Environment page.",
    createFailed: "Failed To Create The Connection: {message}",
    updatedToast: "Connection “{name}” Updated",
    updateFailed: "Failed To Update The Connection: {message}",
    tenantsFetched: "Fetched {count} Tenants",
    fetchTenantsFailed: "Failed To Fetch Tenants: {message}",
    draftTenantsFetched: "Account works; {count} tenants discovered. Save the connection now.",
    connectFailed: "Failed To Connect And Fetch Tenants: {message}",
    tenantPreviewSaved: "Tenant Preview (Saved Connection)",
    tenantPreviewDraft: "Tenant Preview (Not Saved Yet)",
    tenantPreviewHint: "{count} in total. Pick the specific tenant and AI Engineer when binding the project environment.",
    tenantPreviewLimit: "Showing the first {count} only…",
    column: {
      name: "Name",
      platform: "Platform URL",
      account: "Account",
    },
  },
  targets: {
    pageTitle: "{name} · Environments",
    pageDescription: "Bind a platform URL and tenant for each environment stage. Pick the AI Engineer under that tenant when starting an evaluation.",
    unsavedGuard: "The environment binding form has unsaved changes that will be lost if you leave.",
    formNotOpen: "The Form Is Not Open",
    passwordRequired: "After changing the platform URL or account, enter the password before fetching tenants; renaming only does not need a refetch",
    missingBinding: "Missing Binding",
    createdToast: "Environment Binding “{name}” Created",
    createFailed: "Failed To Create: {message}",
    updatedToast: "Environment Binding “{name}” Updated",
    updateFailed: "Failed To Update: {message}",
    defaultSetToast: "“{name}” Is Now The Default Environment",
    defaultFailed: "Failed To Set The Default: {message}",
    disabledToast: "Environment Binding Disabled",
    disableFailed: "Failed To Disable: {message}",
    deletedToast: "Environment Binding Deleted",
    deletedDisabledToast: "Environment Binding Removed (Disabled Because Historical Runs Exist)",
    deleteFailed: "Failed To Delete: {message}",
    readConnectionFailed: "Failed To Read The Connection Settings: {message}",
    selectTenantFirst: "Fetch And Select A Tenant First",
    loadingProject: "Loading Project",
    loadingProjectDescription: "Fetching environment bindings.",
    projectUnavailable: "The Project Cannot Be Opened",
    backToProjects: "Back To Projects",
    lastSynced: "Last Synced {time}",
    refreshedToast: "Environment Bindings Refreshed",
    addBinding: "Add Binding",
    loadingTitle: "Loading Environment Bindings",
    loadingDescription: "Platform URLs and tenants grouped by environment stage.",
    errorTitle: "Failed To Load Environment Bindings",
    emptyTitle: "No Environment Binding Configured",
    emptyReadOnly: "Read-only projects cannot add bindings.",
    emptyDescription: "Add an environment stage + platform URL + tenant to this project, then AI Engineers can be fetched when creating a run.",
    createFirst: "Configure The First Environment",
    stageCount: "{count} Environment Bindings",
    stageCountPaged: "{shown} On This Page / {total} Environment Bindings",
    tableLabel: "{stage} Environment Bindings",
    default: "Default",
    setDefault: "Set As Default",
    disable: "Disable",
    disableHint: "Once disabled it no longer appears in run options",
    lastActiveDisableHint: "This is the only enabled binding; disabling it blocks new runs. Add another binding first",
    lastActiveDeleteHint: "This is the only enabled binding and cannot be deleted; add another binding, set it as default, then delete this one",
    lastActiveTitle: "Only One Enabled Binding Left",
    lastActiveBody: "To keep the project runnable, the only enabled binding cannot be disabled or deleted. Add another binding and set it as default first.",
    nextStepTitle: "Next Step",
    nextStepPrefix: "Go To",
    nextStepSuffix: ": pick an environment → fetch AI Engineers for that tenant → select and start.",
    createTitle: "Add Environment Binding",
    editTitle: "Edit “{name}”",
    bindingFallback: "Environment Binding",
    createDescription: "Fill in the platform URL and account → fetch tenants → select and save. Only one enabled binding is kept per environment stage.",
    editDescription: "Rotate the platform URL and credentials without deleting and recreating. Leave the password blank to keep it unchanged.",
    saveBinding: "Save Environment Binding",
    stage: "Environment Stage",
    displayName: "Display Name",
    displayNamePlaceholder: "For Example: Dev · Tesa",
    platformUrl: "Platform URL / Login Page",
    setDefaultEnv: "Set As Default Environment",
    setDefaultEnvHelp: "New Runs Preselect This Binding",
    fetching: "Fetching…",
    connectAndFetch: "Connect And Fetch Tenants",
    selectTenant: "Select Tenant",
    chooseTenant: "Choose A Tenant",
    keepTenant: "Keep The Current Tenant, Or Fetch First",
    fetchTenantsFirst: "Fetch Tenants First",
    discardTitle: "Discard Unsaved Environment Binding Changes?",
    discardDescription: "Closing the editor discards the platform URL, account, password and tenant selection you entered. Saved bindings are unaffected.",
    deleteTitle: "Delete Environment Binding “{name}”?",
    deleteTitleFallback: "Delete environment binding?",
    deleteDescription: "If there are no historical runs the connection settings are cleaned up as well; otherwise the binding is disabled and the records are kept.",
    deleteDetails: "To rotate a password or change the URL, use Edit instead of deleting.",
    column: {
      connection: "Connection / Access Config",
      tenant: "Tenant",
    },
  },
  suitesPage: {
    title: "Suite Library",
    description: "Import or pick a suite to inspect question content, question version sources and tag snapshots.",
    globalDescription: "Search suites across projects and inspect their questions, versions and run readiness; import and compose inside the owning project.",
    globalEmptyDescription: "No suites are available yet; create or import one from a project's suite page.",
    actionFailed: "Action Failed, Please Retry Later",
    pickFile: "Select an .xlsx suite file",
    pickProject: "Select The Project That Owns The Suite",
    parsed: "The suite was parsed. Resolve conflicts and confirm the write.",
    imported: "Suite Import Finished",
    uploadFailed: "Upload Failed: {message}",
    savedToast: "Suite Details Saved",
    saveFailed: "Failed To Save The Suite: {message}",
    idCopied: "Suite ID Copied",
    copyFailed: "Copy Failed, Please Copy The Suite ID Manually",
    deletedToast: "Suite Deleted",
    deleteFailed: "Failed To Delete The Suite: {message}",
    rebuiltSuiteName: "{name} · Current Versions",
    rebuiltToast: "Created suite “{name}” from the current question-bank versions; the original suite keeps its versions",
    rebuildFailed: "Failed To Rebuild The Suite: {message}",
    loadingTitle: "Loading Suites",
    loadingDescription: "Reading the suite list and question details.",
    errorTitle: "Failed To Load Suites",
    importDialogTitle: "Import Test Suite",
    importDialogDescription: "Choose and parse an Excel file. If conflicts are found, review them in the project question bank before confirming the import.",
    importToProject: "Import Into Project",
    chooseProject: "Select A Project First",
    globalImportHint: "The global suite page only supports search and navigation. Choose the owning project before importing, or go to the project-level suites page.",
    projectsLoadFailed: "Failed to load projects; import is unavailable for now.",
    backToContent: "Back To Content",
    backToSuites: "Back To Suite Library",
    downloadTemplate: "Download The Unified Excel Template",
    closeImportStatus: "Close Import Status",
    missingTitle: "The Suite Does Not Exist Or Was Deleted",
    missingDescription: "The suite ID “{id}” in the link is no longer valid. Pick an existing suite or import a new Excel suite.",
    emptyTitle: "No Suites Yet",
    emptyDescription: "The unified template supports both single-turn and multi-turn questions. Fill in questions and turns, import, review conflicts and confirm the write to create a suite.",
    allSuites: "All Suites",
    listSummary: "{count} Suites · Sorted By {sort}",
    searchSuites: "Search Suites",
    searchPlaceholder: "Search Name Or ID",
    projectFilterAria: "Filter Suites By Project",
    caseCountSource: "{count} questions · {source}",
    defaultProject: "Default Project",
    temporarySuite: "Temporary Suite",
    updatedAt: "Updated {time}",
    noMatchingSuites: "No matching suites.",
    loadingCases: "Loading Questions",
    detailErrorTitle: "Failed To Load Suite Details",
    suiteName: "Suite Name",
    suiteNameRequired: "The Suite Name Cannot Be Empty",
    suiteType: "Suite Type",
    owningProject: "Owning Project",
    projectImmutable: "Project Ownership Cannot Be Changed Directly",
    detailsUnsaved: "Details Not Saved Yet",
    sourceLabel: "Source",
    cases: "Questions",
    caseCount: "{count}",
    saveDetails: "Save Details",
    createRun: "Create Run",
    editDetails: "Edit Details",
    moreActions: "More Actions",
    copySuiteId: "Copy Suite ID",
    deleteSuite: "Delete Suite",
    pinnedTitle: "This Suite Uses The Question Versions Selected When It Was Created.",
    legacyTitle: "Question Content Of Legacy Suites Is Read-Only.",
    staleHint: "{count} questions have newer versions in the bank; adopting the current versions creates a new suite instead of silently rewriting this one.",
    pinnedHint: "To change content, create a new version in the project question bank, then create a suite that uses it.",
    legacyHint: "To keep question versions and historical evaluations reproducible, import or migrate the content into the owning project bank and rebuild the suite.",
    rebuildWithCurrent: "Rebuild With Current Versions",
    goToBank: "Go To Project Bank",
    migrateToBank: "Migrate To Project Bank",
    caseSearchPlaceholder: "Search Question ID Or Question",
    statusFilterAria: "Status Filter",
    refreshingCases: "Refreshing Questions",
    caseSummary: "Showing {shown} / {total} · Runnable {runnable} · Skipped {skipped}",
    caseSummaryStale: " · Newer Version {count}",
    caseSummarySort: " · Sort: Original Question Order In The Suite",
    notPinned: "No Version Recorded",
    upgradableSuffix: " · Newer Version Available",
    pinnedSuffix: " · In Use",
    viewQuestion: "View Question",
    readOnlyCase: "Read-Only Legacy Question",
    noMatchingCases: "No Matching Questions",
    noCases: "This Suite Has No Questions Yet",
    caseDetailTitle: "{id} · Question Details",
    caseDetailPosition: "{index} / {total} In The Current Filter",
    previousCase: "Previous",
    nextCase: "Next",
    caseDetailMeta: "Version Used: {version} · Status: {status}",
    source: {
      questionBank: "Created From Project Bank",
      excel: "Excel Import",
      pinned: "Project-Bank Version",
      manual: "Maintained Manually",
    },
    caseStatus: {
      runnable: "Runnable",
      questionDisabled: "Question Disabled",
      questionArchived: "Question Archived",
      caseDisabled: "Question Disabled",
      skipped: "Skipped",
    },
    column: {
      pinnedVersion: "Version Used",
      question: "Question",
      gtAnswer: "Ground-Truth Answer",
    },
    confirm: {
      leaveTitle: "Discard Unsaved Changes?",
      leaveDescription: "The suite has unsaved changes that will be lost if you leave.",
      leaveConfirm: "Discard And Leave",
      upgradeTitle: "Rebuild The Suite With The Current Bank Versions?",
      upgradeDescription: "A new suite will be created from “{name}”, using the current bank versions of {count} questions. The original suite keeps its existing versions.",
      upgradeConfirm: "Confirm Rebuild",
      rebuilding: "Rebuilding…",
      deleteRunsTitle: "Delete Historical Runs As Well?",
      deleteRunsDescription: "“{name}” has {count}historical run records. Continuing deletes those runs and their reports, and cannot be undone.",
      deleteRunsConfirm: "Delete Suite And Runs",
      runCount: "{count} ",
      deleteTitle: "Delete This Suite?",
      deleteDescription: "Deleting “{name}” removes the suite and its questions. This cannot be undone.",
    },
  },
  profiles: {
    pageTitle: "{name} · Evaluation",
    pageDescription: "Configure the evaluation framework, model credentials, scoring rules, runtime defaults and recommended prompts per project.",
    unsavedGuard: "The evaluation profile has unsaved changes that will be lost if you leave.",
    noModel: "No Model Configured",
    baseConfigured: "Base URL Configured",
    baseMissing: "Base URL Missing",
    keyConfigured: "API Key Configured",
    keyMissing: "API Key Missing",
    promptNamed: "Prompt {name}",
    promptSpecified: "Prompt Specified",
    promptDefault: "Default Prompt",
    defaultRepeat: "Default Repeat {count}",
    createdToast: "Evaluation Profile “{name}” Created",
    updatedToast: "Evaluation Profile Updated To V{version}",
    deletedToast: "Evaluation Profile Deleted",
    disabledInsteadToast: "The profile is referenced, so it was disabled instead",
    noChangeToast: "No changes; no new version created",
    blockDeleteReason: "“{name}” is the project default evaluation profile. Choose another profile or clear the default under Default evaluation first, then delete it.",
    blockDeleteTitle: "The Project Default Profile Cannot Be Deleted",
    gotIt: "Got It",
    goChangeDefault: "Change The Default",
    loadingProjectDescription: "Fetching evaluation profiles.",
    managePrompts: "Manage Prompt Library",
    refreshedToast: "Evaluation Profiles Refreshed",
    create: "New Profile",
    createFirst: "Create The First Profile",
    promptsUnavailable: "The project prompt library is unavailable: {message}. You can still create a profile with the system default prompt.",
    loadingTitle: "Loading Evaluation Profiles",
    loadingDescription: "Reading the evaluation profiles of this project.",
    errorTitle: "Evaluation Profiles Are Unavailable",
    emptyTitle: "No Evaluation Profile Yet",
    emptyReadOnly: "Read-only projects cannot add profiles.",
    emptyDescription: "Once created, the profile can be used in new runs and project settings.",
    tableLabel: "Evaluation Profiles",
    projectDefault: "Project Default",
    readOnlyShared: "Read-Only Shared",
    createTitle: "New Evaluation Profile",
    editTitle: "Edit Evaluation Profile",
    createDescription: "Fill in the evaluation skeleton section by section; recommended prompts come from this project prompt library.",
    editDescription: "Editing “{name}”. Saving with changes creates v{version}; without changes the version stays. Historical run snapshots are unaffected.",
    saveAndBump: "Save And Bump To V{version}",
    discardTitle: "Discard Unsaved Evaluation Profile Changes?",
    discardDescription: "Closing the editor discards the model, key, scoring rules and runtime defaults you entered. Saved versions are unaffected.",
    versionConfirmTitle: "Create Evaluation Profile V{version}?",
    versionConfirmDescription: "After saving, new runs use the new version defaults; historical run snapshots are not rewritten.",
    backToEdit: "Back To Editing",
    confirmBump: "Confirm Bump To V{version}",
    deleteTitle: "Delete Evaluation Profile “{name}”?",
    deleteTitleFallback: "Delete evaluation profile?",
    deleteDescription: "If it is referenced by runs it is disabled instead of physically deleted. The project default profile cannot be deleted directly.",
    deleteDetails: "Current version v{version}. Make sure no run path depends on this profile before deleting.",
    namePlaceholder: "Default Judge Profile",
    evaluatorKind: "Evaluation Framework",
    judgeBaseUrl: "Judge Model Base URL",
    judgeBaseUrlHelp: "API base URL used for judge calls, for example an OpenAI-compatible gateway.",
    judgeModel: "Judge Model",
    judgeModelPlaceholder: "Model ID",
    judgeApiKey: "Judge Model API Key",
    judgeApiKeyPlaceholder: "Cannot Be Shown Again After Saving",
    judgeApiKeyHelp: "Leave blank to keep the saved key unchanged.",
    scoringRule: "Scoring Rule",
    scoringRuleSingleTurn: "Single-Turn Q&A Scoring",
    passThreshold: "Pass Threshold (0–100)",
    passThresholdHelp: "A single score ≥ the threshold counts as passed. For example 60 means 60 points to pass.",
    maxScore: "Maximum Score",
    maxScoreHelp: "Upper bound of the scoring scale, usually 100.",
    consistencyThreshold: "Consistency Threshold (%)",
    consistencyThresholdHelp: "Lower bound of the score agreement ratio across repeats; 100 requires full agreement.",
    agentConcurrency: "AI Engineer Concurrency",
    agentConcurrencyHelp: "Number of simultaneous requests to the target AI Engineer, 1–50. Higher values speed up batches but hit rate limits sooner.",
    agentRetry: "AI Engineer Retry Attempts",
    agentRetryHelp: "Extra retries after a question fails, 0–10. 0 means no retry and the question is marked failed.",
    judgeConcurrency: "Judge Concurrency",
    judgeConcurrencyHelp: "Number of simultaneous judge model calls, 1–100. Higher values consume quota and cost faster.",
    judgeRetry: "Judge Retry Attempts",
    judgeRetryHelp: "Extra retries after a judge call fails, 0–10. Each retry consumes another model call.",
    repeatRuns: "Default Repeat Count",
    repeatRunsHelp: "How many times each question is executed (N).",
    repeatMode: "Repeat Execution Mode",
    repeatModeParallel: "Parallel (Recommended)",
    repeatModeSequential: "Sequential",
    repeatConcurrency: "Per-Question Repeat Concurrency",
    repeatConcurrencyHelp: "How many of the N repeats of one question run at the same time. Effective concurrency ≤ min(repeat count, this value).",
    projectPrompt: "Project Prompt",
    promptUnset: "Unspecified; use the system default at runtime",
    promptsLoading: "Loading the project prompt library…",
    promptsHelp: "The list comes from this project prompt library. Maintain it on the Prompts page and see which profiles reference each prompt.",
    evaluator: {
      toolCall: "Tool Call",
      llmJudgeCurrent: "LLM Judge (Current)",
      ragasPlanned: "RAGAS (Planned)",
      toolCallPlanned: "Tool Call Correctness (Planned)",
      plannedHint: "Planned; not usable for runs yet",
      help: "Planned frameworks cannot be selected; choosing one would fail the run precheck.",
    },
    section: {
      basic: "Basics + Evaluation Framework",
      basicHint: "The profile defines how to evaluate; the production-ready framework today is LLM Judge.",
      model: "Model And Credentials (LLM Judge)",
      modelHint: "Used only for judge model calls; the key is never echoed after saving.",
      scoring: "Scoring Rules",
      scoringHint: "Decides how a single result passes and how stable repeated runs must be.",
      runtime: "Runtime Defaults",
      runtimeHint: "The new-run page inherits these defaults; high concurrency and retries amplify external call pressure.",
      prompt: "Recommended Default Prompt",
      promptHint: "Used by LLM Judge only; new runs can still pick or edit a prompt temporarily without writing back here.",
    },
    diff: {
      name: "Name: {before} → {after}",
      model: "Model: {before} → {after}",
      apiKey: "Judge Model Key: Will Be Updated",
      passThreshold: "Pass Threshold: {before} → {after}",
      consistency: "Consistency Threshold: {before} → {after}",
      repeatRuns: "Default Repeat Count: {before} → {after}",
      repeatConcurrency: "Per-Question Repeat Concurrency: {before} → {after}",
      generic: "Field changes detected (base URL, prompt or runtime defaults)",
    },
    column: {
      kind: "Framework",
      version: "Version",
      summary: "Summary",
    },
  },
  runDetailPage: {
    singleReport: {
      modularTitle: "Modular Report", modularHint: "Five modules are calculated independently; unavailable data is shown as -.", viewAria: "Report Views", single: "Single Report", history: "Dataset History", compare: "Report Comparison",
      questionRepeat: "{questions} Questions · {repeats} Repeats Each", snapshotTitle: "Run Versions And Prompt", snapshotHint: "The report preserves the evaluated platform's runtime snapshot and is not rewritten by current configuration changes.", frozen: "Runtime Snapshot", agentVersion: "AI Engineer Version", platformVersion: "Platform Version", promptVersion: "Evaluated-Platform Prompt Version", repeatCount: "Repeat Count", snapshotValue: "Recorded At Runtime", unrecorded: "Not Recorded For This Run", repeatUnit: "Times / Question", runPrompt: "Evaluated-Platform Prompt Snapshot For This Run", currentPrompt: "Current Evaluated-Platform Prompt", notInSnapshot: "Not A Run Snapshot", currentPromptHint: "The current evaluated-platform prompt is outside this run snapshot, so the report does not infer its content.", targetPromptVersionHint: "Recorded From The Evaluation Target", targetPromptMissingHint: "This run saved the evaluated-platform prompt version but not its body.",
      moduleAria: "Report Metrics And Run Versions", modulePickerTitle: "Core Metrics", modulePickerHint: "Select a metric to open its analysis module. Answer quality is shown first.", versions: "Run Versions", versionsHint: "Shows the test suite, environment, AI Engineer, platform, prompt, and repeat configuration actually used by this report.", viewDetails: "View Details", aiEngineer: "Evaluated AI Engineer", environment: "Environment", runtimeConfiguration: "Run Configuration", testSuite: "Test Suite", runId: "Run ID", runtimeSnapshotId: "Runtime Snapshot Identifier", questionConfiguration: "Question Configuration", overview: "Run Versions", overviewHint: "Shows only the versions and configuration snapshot used by this run.", notApplicable: "Not Applicable", platform: "Platform Stability", stability: "Answer Stability", experience: "User Experience", cost: "Cost", quality: "Answer Quality", module: { platform: "Returns And Valid Judging", stability: "Repeat-Answer Consistency", experience: "Response Latency", cost: "Token Usage", quality: "Valid-Answer Quality" },
      platformHint: "Evaluates AI Engineer execution, answer return, and Judge scoring without counting technical failures as quality scores.", failedQuestions: "{count} Incomplete Questions", successRate: "Question Completion Rate", executionErrors: "AI Engineer Execution Errors", timeouts: "Timeouts Without Answer", judgeValidRate: "Valid Judge Score Rate", definition: "Definition", platformDefinition: "Completed questions / total questions. A normal 0-score response still counts; if any repeated answer is incomplete, the question is incomplete.", qualityExclusion: "{count} incomplete questions are excluded from the answer-quality average.", failureReasons: "Failure Reasons", failure: { agentError: "AI Engineer Execution Error", timeout: "Timeout Without Final Answer", judgeFailure: "Judge Service Failure" },
      stabilityHint: "Compares conclusions and content across repeated runs of the same question.", stabilityNotTestedHint: "Each question was answered only once in this report, so there are no repeated answers to compare.", stabilityMinimumRepeatHint: "Answer stability requires at least 2 answers per question in the same report; 3 repeats are recommended before using it for stability decisions.", notTested: "Not Tested", repeatBadge: "{count} Answer(S) Per Question", overallConsistency: "Overall Answer Consistency", component: { conclusion: "Core Conclusion", facts: "Key Facts", citations: "Citations", scoreVariance: "Score Variance" }, fluctuatingQuestions: "Fluctuating Questions",
      experienceHint: "Separates first response, complete answer, and tool latency; missing telemetry is not inferred.", firstResponseP50: "First Response P50", firstResponseP95: "First Response P95", fullResponseP50: "Complete Answer P50", fullResponseP95: "Complete Answer P95", toolDuration: "Tool Duration", answerDuration: "From Recorded Answer Duration", experienceDefinition: "Complete-answer latency uses only recorded AI Engineer response time and excludes queue and Judge time.",
      costHint: "Shows recorded or deterministically estimated tokens; money uses the fixed estimated unit price stored with the snapshot.", totalTokens: "Total Tokens", runTotal: "Run Total", avgTokens: "Average Tokens", perCompleteAnswer: "Per Complete Answer", inputShare: "Input Share", outputShare: "Output Share", estimatedCost: "Estimated Cost", estimatedCostNote: "Fixed Estimated Unit Price (USD)", tokenComposition: "Input And Output Token Composition",
      qualityHint: "Each question averages its non-null quality dimensions; the report averages the 39 question quality scores.", validQuestions: "{count} Valid Questions", overallScore: "Answer Quality Average", completeOnly: "Not-Applicable Dimensions Are Excluded", dimension: { correctness: "Conclusion, Fact, And Calculation Correctness", depth: "Information Completeness And Depth", conditions: "Conditions, Assumptions, And Exceptions", scope: "Parties, Roles, And Interface Scope", actionability: "Actionability And Risk Guidance", evidence: "Citations, Evidence, And Traceability", synthesis: "Cross-Document Synthesis And Conflicts" }, scoringGate: "Scoring Method", scoringGateHint: "Each question averages only non-null dimensions. Not-applicable dimensions display - and are excluded from the denominator; platform failures and incomplete answers are not automatically scored as zero.", viewRelatedResults: "View Related Evaluation Results", platformDrilldownHint: "Open incomplete results to inspect failed answers and runtime evidence.", stabilityDrilldownHint: "Open fluctuating questions to compare every complete answer.", experienceDrilldownHint: "Inspect complete-answer latency for each question in evaluation results.", costDrilldownHint: "Inspect recorded tokens for each question in evaluation results.", qualityDrilldownHint: "Open below-threshold questions to inspect the expected answer, response, and Judge conclusion.", repeatAnswersCount: "{count} Repeated Answers", table: { consistency: "Consistency Score", responseTime: "First / Complete", tokens: "Tokens", qualityScore: "Question Quality", judgeConclusion: "Judge Conclusion", returned: "Returned", stable: "Stable", attention: "Needs Attention", noIssues: "-" },
    },
    agentContextTitle: "AI Engineer Context",
    agentContextContract: "Current Route: {route}",
    agentContextLegacy: "Legacy Context ID: {id}",
    agentContextSection: "Page Section: {section}",
    notFound: "The run record does not exist or was deleted",
    loadFailed: "Failed To Load Run Details",
    missingRunId: "Run ID Is Missing",
    backTo: "Back To {target}",
    backToArrow: "← Back To {target}",
    loadingReport: "Loading Report Details",
    loadingRun: "Loading Run Details",
    openReportFailed: "Cannot Open The Report Details",
    openRunFailed: "Cannot Open The Run Details",
    errorHint: "{message}. Retry or go back to {target}.",
    retryCreated: "Retry Run Created: {id}",
    retryFailed: "Failed To Rerun: {message}",
    agentRetryQueued: "AI Engineer Sessions Queued For Retry",
    judgeRetryQueued: "Judge Evaluation Queued For Retry",
    stageRetryFailed: "Stage Retry Failed: {message}",
    caseRetryStarted: "Technical Retry Started For Question {caseId} Run {index}",
    caseRetryFailed: "Question Retry Failed: {message}",
    downloadStarted: "{kind} Download Started",
    downloadFailed: "Download Failed: {message}",
    downloadDisabledHint: "The run has not finished, so there is nothing to download yet",
    downloading: "Downloading",
    downloadJson: "Download JSON",
    downloadExcel: "Download Excel",
    viewFailures: "View Failed Questions ({turns} Turns / {cases} Questions)",
    viewLogs: "View Logs",
    openEvaluationResult: "View Evaluation Results",
    openEvaluationReport: "View Evaluation Report",
    checkEnvSettings: "Check Environment Settings",
    creating: "Creating",
    rerun: "Rerun",
    reportDetail: "Test Report Details",
    runResult: "Run Results",
    taskDetail: "Test Task Details",
    resultDetail: "Test Result Details",
    repeatBadge: "{count} repeats",
    needsAttention: "Needs Attention",
    runIdLabel: "Run ID: {id}",
    viewTechnicalInfo: "View Technical Details",
    archivedIncompleteTitle: "Archived, But With Incomplete Results",
    archivedIncompleteBody: "Incomplete results are excluded from the average score and pass rate, and show as —; only those results can be retried technically.",
    caseRetryRunningTitle: "Retrying Incomplete Results",
    caseRetryRunningBody: "Once the retry finishes, the effective results, aggregated scores and download files of this run are updated automatically; the original failure records are kept.",
    reportConclusion: "Report Conclusion",
    resultSummary: "Result Overview",
    resultAttentionHint: "Some results are below threshold, incomplete, or inconsistent. Use the metrics below to filter them quickly.",
    resultHealthyHint: "No result currently needs attention. You can still review each answer and its scoring evidence.",
    attentionHint: "Handle failing, incomplete and inconsistent results first, then decide whether to change questions, change the AI Engineer, or rerun.",
    noAttentionHint: "No issues need attention right now; you can still drill into the question queue to check answers.",
    viewCaseQueue: "View Question Queue",
    clickSkipped: "Click To View Skipped Questions",
    clickDrilldown: "Click To Drill Into Questions",
    runProgress: "Run Progress",
    stageLabel: "Stage: {stage}",
    elapsed: "Elapsed {duration}",
    stepsDone: "{done} / {total} Execution Steps Completed",
    currentCase: " · Current Question {caseId}",
    notStartedTitle: "The Run Has Not Started Yet",
    notStartedBody: "The pre-flight configuration check failed, so none of the planned {total} execution steps called the AI Engineer or judge. Fix the environment settings and create the run again.",
    noResultTitle: "No Evaluation Result Yet",
    noResultBody: "The task failed pre-flight validation, so there are no question results to analyze. Return to the evaluation task, fix the configuration, and rerun.",
    resultMetrics: "Test Result Metrics",
    reportMetrics: "Report Metrics",
    runMetrics: "Run Metrics",
    reportViews: "Report Detail Views",
    runViews: "Run Detail Views",
    sourceLinks: "Sources And Links",
    runInfo: "Run Information",
    taskExecutionInfo: "Run Configuration",
    taskExecutionHint: "Shows the evaluation target, question scope, execution snapshot, and scoring configuration actually used by this task.",
    taskConfigTarget: "Evaluation Target",
    taskConfigExecution: "Execution Snapshot",
    taskConfigEvaluation: "Scoring Configuration",
    taskCompletedTitle: "Test Task Completed",
    taskCompletedHint: "Execution monitoring is complete. Continue to the question-level results or the aggregated analysis.",
    viewSkipped: "View The {count} Skipped Questions",
    sourceDeepLinks: "Report Source Deep Links",
    openSuite: "Open Suite",
    openBank: "Open Question Bank",
    openRun: "View Original Run",
    openAttentionQueue: "Open The Attention Question Queue",
    judgePromptBody: "Judge Prompt Body",
    judgePromptTitle: "Judge Prompt Used",
    promptBody: "Prompt Body",
    viewPromptBody: "View Prompt Body",
    opsSecondary: "Ops Actions (Secondary)",
    stageRetry: "Stage Retry",
    multiTurnRetryTitle: "Multi-Turn Runs Must Be Retried As A Whole",
    multiTurnRetryBody: "To keep the full conversation state within one session, the AI Engineer and judge cannot be retried separately. Use Rerun at the top of the page, or retry the whole question from the results table.",
    retryAgent: "Retry AI Engineer Sessions",
    retryJudge: "Retry Judge Evaluation",
    skippedTitle: "Questions Skipped In This Run",
    skippedDescription: "The execution list was frozen when the run was created; {count} questions were not executed and therefore do not appear in the question queue.",
    skippedCloseLabel: "Close The Skipped Question List",
    skippedNoDetail: "This snapshot does not record skip details per item; see the skip counts in the overview.",
    confirmRetry: "Confirm Retry",
    validation: {
      judgeTitle: "The Judge Service Is Unavailable",
      judgeDescription: "Check the judge base URL, model and credentials of the current environment, then create the run again once verified.",
      agentTitle: "AI Engineer Connection Check Failed",
      agentDescription: "Check the AI Engineer URL, login credentials and tenant configuration of the current environment, then create the run again once connected.",
      genericTitle: "Pre-Flight Configuration Check Failed",
      genericDescription: "Check the current environment configuration and create the run again once it passes.",
    },
    confirm: {
      fullTitle: "Rerun This Run?",
      fullDescription: "A full run is recreated from the current suite with {count} repeats, which may trigger new model calls.",
      agentTitle: "Retry AI Engineer Sessions?",
      judgeTitle: "Retry Judge Evaluation?",
      stageDescription: "Run: {name}. The current stage is called again and may trigger new model calls.",
      caseTitle: "Retry Question “{caseId}” Run {index}?",
      caseDescription: "This incomplete result is repaired within the current run and the summary is recomputed; the original failure record stays archived.",
    },
    tab: {
      conclusion: "Conclusion",
      overview: "Overview",
      caseQueue: "Question Queue",
      results: "Result Details",
    },
    metric: {
      avgScore: "Average Question Score",
      scoreValue: "{score} Pts",
      avgScoreHint: "Counts Complete Question Executions Only; Incomplete Results Are Excluded From The Average",
      passRate: "Pass Rate",
      passThreshold: "Pass Threshold {threshold} Pts",
      failedCases: "Aggregated Failing Questions",
      failedCasesHint: "{count} Questions In Total · Click To Filter",
      completionRate: "Question Completion Rate",
      completionHint: "{done} / {total} Questions Completed Normally · Filter Incomplete",
      consistency: "Consistency",
      consistencyHint: "Mean Per-Question Consistency Score · Click To Filter",
    },
    fact: {
      envAgent: "Environment / AI Engineer",
      environment: "Environment",
      aiEngineer: "AI Engineer",
      agentContext: "AI Engineer Context",
      noAgentContext: "No Additional Context",
      elapsedActive: "Elapsed Run Time",
      elapsedDone: "Total Run Time",
      elapsedActiveHint: "From Run Start To Now, Accumulating While Running",
      elapsedDoneHint: "From Run Start To Result Completion",
      completedQuestions: "Completed Questions",
      completedQuestionsHint: "A normal 0-score response still counts; only incomplete execution is excluded",
      repeatCount: "Repeat Configuration",
      repeatCountValue: "{count} Times Per Question",
      repeatCountHint: "Frozen When The Task Was Created",
      currentStage: "Current Stage",
      currentStageHint: "Taken from task execution state; missing stages are not inferred",
      agentTotal: "Total AI Engineer Execution Time",
      agentSamples: "Accumulated Over {count} Valid Executions",
      agentAvg: "Average AI Engineer Execution Time",
      agentAvgHint: "Computed Over Valid Executions",
      tokenUsage: "Token Usage",
      noRecord: "No Record",
      tokens: "{value} tokens",
      tokenMissingHint: "This run did not report model usage; the backend must record it in the run summary",
      tokenSplit: "Input {prompt} · Output {completion}",
      tokenCombined: "Judge And AI Engineer Calls Combined",
      estimatedCost: "Estimated Cost",
      costMissingHint: "The Backend Did Not Report A Cost Estimate",
      costHint: "Estimated From Model Unit Prices, For Reference Only",
      runnableQuestions: "Questions Included",
      runnableCount: "{runnable} / {total}",
      frozenHint: "Frozen when the run was created; later question-bank changes do not apply",
      skippedByQuestion: "Skipped: Question Disabled",
      archivedExtra: "{count} More Questions Are Archived",
      questionDisabled: "Question Disabled",
      skippedBySuite: "Skipped: Suite Question Disabled",
      suiteItemDisabled: "The suite question was already disabled when the run was created",
      judgePrompt: "Judge Prompt",
    },
    dimension: {
      title: "Accuracy By Category",
      description: "Pass rate of executed turns grouped by the primary question category, shown together with question counts to avoid misleading small-sample percentages.",
      count: "{count} categories",
      empty: "This Suite Has No Primary Category Tags",
      emptyHint: "Tag questions in the bank with types such as data lookup, knowledge lookup or mixed scenarios; later runs then produce category metrics automatically.",
      questions: "Questions",
      passed: "Passed",
      failed: "Failed",
      avgScore: "Average Score",
    },
    issues: {
      title: "AI Engineer Issue Signals",
      caseCount: "{count} questions",
      pendingReview: "Pending Review",
      empty: "No AI Engineer issue directly tied to user questions.",
    },
    skipped: {
      index: "#",
      reason: "Skip Reason",
      question: "Question",
    },
  },
  results: {
    title: "Test Results",
    description: "Review per-question outputs, completeness, scores, and pass rates. Use Reports for interpreted findings.",
    total: "{count} results",
    searchAria: "Search Evaluation Results",
    searchPlaceholder: "Search Task, ID, AI Engineer, Or Test Suite",
    filterSummary: "{count} Filtered Results",
    completenessFilterAria: "Filter By Result Completeness",
    allCompleteness: "All Completeness",
    complete: "Complete Results",
    incomplete: "Incomplete Results",
    suiteFilterAria: "Filter Evaluation Results By Suite",
    agentFilterAria: "Filter Evaluation Results By AI Engineer",
    loadingTitle: "Loading Evaluation Results",
    loadingDescription: "Reading tasks that already produced output for this project.",
    errorTitle: "Could Not Load Evaluation Results",
    errorDescription: "Results are temporarily unavailable. Try again.",
    noMatchTitle: "No Matching Evaluation Results",
    noMatchDescription: "Adjust the search terms.",
    emptyTitle: "No Evaluation Results Yet",
    emptyDescription: "Create and execute an evaluation task to see its results here.",
    tableLabel: "Project Evaluation Results",
    column: { task: "Test Task", status: "Status", score: "Average Score", passRate: "Pass Rate", questions: "Questions / Repeats", finished: "Finished" },
    questionCount: "{count} / {repeats}",
    viewResult: "View Result",
  },
  reports: {
    title: "All Test Reports",
    projectTitle: "Test Reports",
    description: "Global search entry. For day-to-day triage prefer the Reports page inside a project; this page is for cross-project lookups.",
    projectDescription: "Interpret evaluation quality, locate problem questions, and decide whether to revise questions or rerun.",
    dataUnavailable: "Report Data Is Unavailable",
    total: "{count} Reports In Total",
    goToProjects: "Go To Projects",
    exportFiltered: "Export Current Filter",
    exportEmpty: "The Current Filter Has No Report To Export",
    exported: "Exported {count} Reports From The Current Filter",
    viewReport: "View Report",
    closeCompareNotice: "Close The Comparison Link Notice",
    compareNotice: "{count} runs from the link no longer exist or have no usable report yet and were removed from the comparison.",
    searchPlaceholder: "Search Report Name Or Run ID",
    searchAria: "Search Reports",
    projectFilterAria: "Filter Reports By Project",
    agentFilterAria: "Filter By AI Engineer",
    allAgents: "All AI Engineers",
    suiteFilterAria: "Filter By Suite",
    allSuites: "All Suites",
    qualityFilterAria: "Filter By Quality Result",
    allQuality: "All Quality Results",
    qualityAttentionOption: "Has Failures, Incomplete Results Or Low Consistency",
    sortAria: "Report Sorting",
    filterSummary: "{shown} / {total} matched",
    compareGuideAria: "Report Comparison Guide",
    compareUnavailable: "Comparison Is Unavailable",
    compareUnavailableHint: "Only 1 report so far. Run another evaluation to compare; you can also open the details to inspect questions.",
    runAgain: "Run Again",
    compareAria: "Report Comparison",
    compareTitle: "Comparison ({count}/4)",
    compareNeedMore: "Select one more report to compare; with fewer than 2, run another evaluation first.",
    compareVerifying: "Verifying question, ground-truth and prompt snapshots…",
    compareIncomparable: "Configurations That Are Not Directly Comparable: {fields}",
    compareSnapshotUnknown: "Some historical reports lack question or prompt snapshots; interpret with care",
    compareComparable: "Questions, ground truths, judge prompt and key evaluation settings match, so the reports are directly comparable",
    compareSummaryOnly: "More than 2 reports are selected, so only the horizontal summary is shown. Select exactly 2 for a trusted version verdict and question changes.",
    compareAnalyzeFailed: "Comparison analysis failed for now. No optimization verdict will be produced.",
    listSeparator: ", ",
    swapBaseline: "Swap Baseline And Candidate",
    clearSelection: "Clear Selection",
    metricColumn: "Metric",
    envJudge: "Environment / Judge",
    turnPassRate: "Turn Pass Rate",
    turnPassRateValue: "Turn Pass Rate {value}",
    repeatDuration: "Repeats / Duration",
    repeatTimes: "{count} times",
    scoringBasis: "Scoring Basis",
    passThresholdValue: "Pass Threshold {value}",
    maxScoreValue: "Max Score {value}",
    caseImproved: "Improved Questions",
    caseRegressed: "Regressed Questions",
    caseSame: "Unchanged Questions",
    caseStatusChanged: "Pass-Status Changes",
    caseLevelChanges: "Question-Level Changes",
    caseLevelHint: "The first report is the baseline and the second the candidate; regressions are shown first by default.",
    onlyRegressed: "Regressed Only",
    allChanges: "All Changes",
    columnCase: "Question",
    baseline: "Baseline",
    candidate: "Candidate",
    change: "Change",
    statusChangedHint: "Pass Status Changed",
    noRegressed: "No regressed questions.",
    caseReview: "Question Review",
    caseReviewHint: "Side-By-Side Review Of The Latest Answers",
    judgeConclusion: "Judge Conclusion",
    comparisonVerdictLabel: "Optimization Verdict",
    scoreDelta: "Average Score Change",
    passRateDelta: "Pass-Rate Change",
    stablePassDelta: "Stable Pass-Rate Change",
    evidenceStrength: "Evidence Strength",
    percentagePoint: " pp",
    comparisonVerdict: {
      stable_improvement: "Stable Improvement: Average Score {score}, Pass Rate {passRate} Pp",
      observed_improvement: "Observed Improvement: Average Score {score}, Pass Rate {passRate} Pp",
      no_clear_change: "No Clear Change Observed",
      mixed_with_regressions: "Overall Improvement With Regressed Questions",
      regressed: "The candidate regressed; review regression questions first",
      incomparable: "These runs do not qualify for a formal comparison",
    },
    comparisonVerdictName: {
      stable_improvement: "Stable Improvement",
      observed_improvement: "Observed Improvement",
      no_clear_change: "No Clear Change",
      mixed_with_regressions: "Improved With Regressions",
      regressed: "Regressed",
      incomparable: "Not Formally Comparable",
    },
    evidence: {
      low: "Low",
      medium: "Medium",
      high: "High",
    },
    comparisonDifference: {
      different_project: "Different Projects",
      different_logical_agent: "Different Logical AI Engineers",
      different_suite: "Different Suites",
      different_suite_snapshot: "Different Question Or Ground-Truth Snapshots",
      different_judge_model: "Different Judge Models",
      different_judge_prompt: "Different Judge Prompts",
      different_evaluation_profile: "Different Evaluation Profile Snapshots",
      different_pass_threshold: "Different Pass Thresholds",
      different_max_score: "Different Maximum Scores",
      different_scoring_rule: "Different Scoring Rules",
      different_stage_code: "Different Environments",
      different_target_id: "Different Run Targets",
      different_repeat_runs: "Different Repeat Counts",
      different_repeat_execution_mode: "Different Repeat Execution Strategies",
      different_case_coverage: "Different Question Coverage",
      incomplete_case_coverage: "Incomplete Question Execution Coverage",
      run_ineligible: "An Incomplete Or Unfinished Run",
      same_environment: "The Same Environment Was Selected For Environment Comparison",
    },
    transition: {
      fixed: "Failed To Stable Pass",
      regressed: "Stable Pass To Fail",
      improved: "Score Improved",
      regressed_score: "Score Decreased",
      same: "unchanged",
      stabilized: "Flaky To Stable",
      destabilized: "Stable To Flaky",
      incomparable: "Incomplete Result",
      baseline_only: "Baseline Only",
      candidate_only: "Candidate Only",
    },
    loadingTitle: "Loading Reports",
    loadingDescription: "Fetching completed evaluation results, please wait.",
    errorTitle: "Failed To Load Reports",
    errorDescription: "The report list is temporarily unavailable.",
    emptyTitle: "No Report Available Yet",
    emptyPending: "{count} runs are still executing or syncing; they appear here once finished.",
    emptyDescription: "Reports appear here automatically once evaluation runs complete.",
    viewActiveRuns: "View Active Runs",
    importSuite: "Import A Suite",
    noMatchTitle: "No Matching Reports",
    noMatchDescription: "Adjust the search keyword or filters.",
    clearAllFilters: "Clear All Filters",
    selectCompare: "Select For Comparison",
    selectCompareAria: "Select {title} For Comparison",
    compareNeedTwo: "At Least 2 Reports Are Required To Compare",
    runIdMeta: "Run ID · {id}",
    finishedMeta: "Finished · {time}",
    filterByAgentTitle: "Show Only Reports Of AI Engineer {agent}",
    consistencyMeta: "Consistency {value}%",
    durationTitle: "Duration {duration}",
    incomplete: {
      cases: "Incomplete Results · {count} Questions",
      runError: "Incomplete Results · Run Error",
      systemErrors: "Incomplete Results · {count} Execution Errors",
      executed: "Incomplete Results · Executed {executed}/{expected} Repeats",
      missingScore: "Incomplete Results · Missing Scores",
    },
    quality: {
      unknown: "No Decisive Result",
      gateFailed: "Quality Gate Failed",
      failedCases: "{count} Aggregated Questions Below Threshold",
      incompleteTitle: "{label} (Same Definition As “Incomplete Results” In The Report Details)",
      consistencyLow: "Consistency Only {value}%",
      attention: "Has Items Needing Attention",
      attentionTitle: "Has Items Needing Attention (Consistency / Result Completeness)",
      passed: "All Passed",
      passedTitle: "All Aggregated Questions Passed",
    },
    cases: {
      executedSkipped: "{executed} Executed · {skipped} Skipped",
      executedSkippedTitle: "{executed} Runnable, {skipped} Skipped{repeats}. See The Report Details For Skip Reasons.",
      runnable: "{executed} / {source}",
      runnableTitle: "This Report Includes {executed} / {source} Questions{repeats}",
      aggregate: "{count} Questions · {repeats} Repeats",
      aggregateTitle: "{count} Aggregated Questions; {repeats} Repeats",
      repeatSuffix: "; {count} repeats",
    },
    compareField: {
      suite: "Suite",
      judgeModel: "Judge Model",
      passThreshold: "Pass Threshold",
      maxScore: "Max Score",
      scoringRule: "Scoring Rule",
      contentSnapshot: "Question / Ground-Truth Snapshot",
      promptSnapshot: "Judge Prompt Snapshot",
    },
    sort: {
      finished: "By Finish Time, Newest First (Default)",
      score: "By Average Question Score (High To Low)",
      passRate: "By Turn Pass Rate (High To Low)",
      failedCases: "By Fewest Failing Questions",
      duration: "By Duration (Long To Short)",
    },
    csv: {
      title: "Report Title",
      runId: "Run ID",
      passRate: "Turn Pass Rate",
      cases: "Question Summary",
      finishedAt: "Finished At",
    },
    column: {
      report: "Report",
      quality: "Quality Result",
      score: "Average Score / Pass Rate",
      completedQuestions: "Completed Questions",
      finished: "Finished / Duration",
    },
  },
  newRun: {
    pickerDescription: "Stay on the current page and configure Project → Environment → AI Engineer → Test suite → Evaluation profile.",
    loadingProjects: "Loading projects…",
    projectsLoadFailed: "Failed To Load Projects: {message}",
    noRunnableProjects: "No project can start an evaluation. The legacy container “default” cannot create runs.",
    goToProjectAdmin: "Go To Project Admin",
    selectProject: "Select A Project",
    selectProjectFirst: "Select A Project To Continue",
    projectStepHint: "After you select a project, only its environments and test assets are loaded.",
    projectLockedHint: "Later configuration has already been entered. Cancel this draft and reopen it to switch projects.",
    pageTitle: "{name} · New Test Task",
    pageDescription: "Pick the environment, AI Engineers, suite and repeat count to start; when the profile is complete only a summary is shown.",
    unnamedEnv: "Unnamed Environment",
    defaultProjectNotice: "The legacy container project “default” cannot start new runs; choose another project.",
    defaultProjectBlocked: "The default project is only a historical data container and cannot start new runs.",
    suiteMissingNotice: "The suite in the link no longer exists or does not belong to this project; pick another one.",
    missing: {
      model: "model",
      baseUrl: "Service URL",
      apiKey: "API Key",
      scoringRule: "Scoring Rule",
      passThreshold: "Pass Threshold",
      maxScore: "Max Score",
      template: "Evaluation Profile Is Missing {field}",
    },
    blocker: {
      selectProfile: "Select An Evaluation Profile",
      defaultProject: "The Default Project Cannot Create Evaluations",
      selectBinding: "Select An Environment Binding",
      loadingAgents: "Loading AI Engineers of the current environment…",
      agentsFailed: "Failed To Load AI Engineers: {message}",
      selectAgent: "Select At Least One AI Engineer",
      noAgents: "No Available AI Engineer Found In This Environment",
      suiteAllSkipped: "This suite has no runnable question ({count} will be skipped)",
      selectSuite: "Select a suite of this project with at least one runnable question",
      profileIncomplete: "{issues}. Complete the evaluation profile first",
      customPrompt: "Enter The Custom Judge Prompt For This Run",
      libraryPrompt: "Select a project prompt or enter a temporary prompt body",
      repeatRange: "Repeat Count Must Be An Integer Between 1 And {max}",
      nameRequired: "High-Volume Runs Require A Run Name",
      storageUnknown: "The data storage connection cannot be confirmed right now",
      settingsUnavailable: "Environment Bindings Or Evaluation Profiles Are Unavailable",
      validateAgentContext: "Validate The Contract Page URL Before Starting",
    },
    fix: {
      configureBinding: "Configure Environment Binding",
      checkBinding: "Check Environment Binding",
      prepareSuite: "Prepare A Suite",
      goToProfiles: "Go To Evaluation Profiles",
      completeProfile: "Complete The Evaluation Profile",
      openSettings: "Open Project Settings",
    },
    hint: {
      retryInAgentArea: "You Can Retry In The AI Engineer Section",
      pickAgents: "Tick The AI Engineers You Want To Test",
      switchEnv: "Switch Environment Or Check The Tenant Configuration",
    },
    evalHint: {
      usingDefault: "Using the project default profile; change it if needed.",
      manual: "A manually selected evaluation profile is in use; change it if needed.",
      fallback: "No project default is set, so a fallback profile is in use; change it if needed.",
      noDefault: "The project has no default evaluation profile; select one first.",
      generic: "The evaluation profile and scoring prompt decide how answers are graded.",
    },
    summary: {
      project: "Project: {name}",
      suite: "Suite: {name}",
      runnable: "Runnable Questions: {count}",
      skipped: "Skipped: {count} ({disabled} Disabled Questions, {archived} Archived Questions, {suiteDisabled} Disabled Suite Items)",
      repeat: "Repeat Count: {count}",
      environment: "Environment: {name}",
      contractContext: "IM Production Current Route: {route}",
      contractContextEmpty: "AI Engineer Context: Empty (Optional)",
      contractContextDisabled: "AI Engineer Context: Disabled For This Project",
      profile: "Evaluation Profile: {name}",
      prompt: "Judge Prompt: {name}{snapshot}",
      promptLibrary: "Project Library",
      promptSnapshot: " (With A Temporary Body Snapshot)",
      promptNotApplicable: "Judge Prompt: Not Applicable For Non-LLM Judges",
      agents: "AI Engineers ({count}): {names}",
      estimatedCalls: "Estimated calls: {count} AI Engineer calls plus the same number of judge calls",
    },
    promptSaved: "Saved As Project Prompt “{name}”",
    agentContextUrl: "IM Production Page URL (Optional)",
    agentContextPlaceholder: "For example https://im.example.com/contracts/123/clauses?clauseId=456",
    agentContextOptionalHint: "Stores and sends the pathname with the complete query. IM Production resolves business entities and page state. Do not enter URLs containing tokens, signatures, or credentials.",
    validateAgentContext: "Validate",
    agentContextResolved: "Current Route Identified: {route}",
    promptSaveFailed: "Failed To Save The Prompt: {message}",
    promptBodyRequired: "Enter A Prompt Body To Save First",
    promptNameRequired: "Enter A Prompt Name",
    batchCreated: "Evaluation Batch Created: {id}",
    runCreated: "Evaluation Run Created: {id}",
    partialTitle: "Some Targets Failed The Pre-Check",
    partialDescription: "No run has been created yet. Confirming skips the ineligible targets and creates runs only for the runnable ones.",
    partialEligible: "{count} targets are runnable.",
    partialNoEligible: "No runnable target right now.",
    partialConfirm: "Skip Ineligible Targets And Create",
    partialCancelled: "Partial Creation Cancelled",
    createFailed: "Failed To Create The Run: {message}",
    suiteRefreshFailed: "Failed To Refresh The Suite Status, Please Retry",
    suiteChangedWithSkipped: "The suite status changed and has no runnable question ({count} will be skipped)",
    suiteChanged: "The suite status changed and has no runnable question",
    confirmSkipTitle: "Skip The Ineligible Targets?",
    confirmCreateTitle: "Create This Evaluation Run?",
    confirmSkipDescription: "Some targets failed the pre-check. Confirming creates runs only for the runnable ones.",
    confirmCreateDescription: "Review the configuration below before starting.",
    start: "Start Evaluation",
    leaveTitle: "Leave The New Evaluation?",
    leaveDescription: "This evaluation has unsaved changes and the draft will be lost.",
    leaveConfirm: "Leave Anyway",
    discardTitle: "Discard This New Evaluation?",
    discardDescription: "The unsaved draft will be lost.",
    discardConfirm: "Discard Draft",
    customThisRun: "Custom For This Run",
    notSelected: "Not Selected",
    backToRuns: "Back To Evaluation Tasks",
    step1Title: "Where And Who To Test",
    step1Hint: "Switching the environment loads that tenant's AI Engineers; then select the targets for this run.",
    step2Title: "What To Test And How Many Times",
    step2Hint: "Choose a suite of this project and confirm the repeat count.",
    envBinding: "Environment Binding",
    configureBindingFirst: "Configure An Environment Binding First",
    bindingUnset: "Not Set (Select An Environment Binding)",
    agentsLabel: "AI Engineers (Multi-Select)",
    agentsAutoLoad: "Loaded Automatically From Environment “{name}”",
    agentsAfterEnv: "AI Engineers of the tenant load automatically once an environment is selected",
    selectAll: "Select All",
    selectAllVisible: "Select All Visible",
    clear: "Clear",
    loadingAgents: "Loading AI Engineers…",
    loadFailed: "Failed To Load: {message}",
    agentSearchPlaceholder: "Search Name, Code Or Role",
    agentFilterAria: "Filter AI Engineers",
    selectAgents: "Select AI Engineers",
    noMatchingAgents: "No matching AI Engineer; adjust the filter",
    selectedCount: "{selected} / {total} selected",
    willCreateRuns: "{count} Separate Runs Will Be Created",
    willCreateOneRun: "1 Run Will Be Created",
    selectToStart: "Tick AI Engineers To Start The Evaluation",
    selectedNone: "0 selected",
    noSuites: "No Suite In This Project Yet",
    suiteUnset: "Not Set (Select A Suite)",
    repeatRuns: "Repeat Count",
    runNameRequired: "Run Name (Required)",
    runNameOptional: "Run Name (Optional)",
    runnableCases: "Runnable Questions",
    willSkip: "Will Skip",
    estimatedCalls: "Estimated Calls",
    estimatedCallsValue: "{count} AI Engineer · {count} Judge",
    pendingAgent: "AI Engineer Pending",
    selectedAgents: "Selected AI Engineers",
    collapseAdvanced: "Collapse Advanced",
    change: "Change",
    projectDefaultSuffix: " (Project Default)",
    fallbackSuffix: " (fallback)",
    promptLabel: "Prompt",
    editedSuffix: " (Temporarily Edited)",
    viewProjectDefault: "View Project Default",
    setProjectDefault: "Set A Project Default",
    createProfileFirst: "Create An Evaluation Profile First",
    profileUnset: "Not Selected (No Project Default)",
    noDefaultProfileHint: "This project has no default evaluation profile; select one here, or ",
    setDefaultLink: "Set A Default",
    periodSuffix: ".",
    profileBlockedPrefix: "The evaluation profile cannot start yet: {issues}. ",
    goComplete: "Complete It",
    profileReady: "Current evaluator: {kind}; the profile meets the prerequisites to run.",
    promptMode: "Prompt Mode",
    promptModeLibrary: "From Library",
    promptLibraryUnavailable: "The Prompt Library Is Unavailable",
    noPrompts: "No Prompt In This Project Yet",
    promptUnset: "Not Set (Select A Prompt)",
    profileRecommended: "Profile Recommends: {name}",
    switchToCustomHint: "Switch to “Custom for this run” to continue.",
    promptPreview: "Full Preview / Temporary Edit",
    promptCustomBody: "Custom Prompt Body For This Run",
    promptPreviewPlaceholder: "Selecting a project prompt fills in the full text, which you can tweak before this run.",
    promptCustomPlaceholder: "Enter the full prompt used by the LLM judge for this run.",
    promptSource: "Source: {name}",
    promptNotSelected: "No Project Prompt Selected Yet",
    promptEditedHint: "; the body was edited temporarily and will not be written back to the library.",
    promptCustomHint: "The custom body is submitted with this run only; save it as a project prompt to reuse it.",
    saveAsProjectPrompt: "Save As Project Prompt",
    promptNamePlaceholder: "e.g. Strict scoring prompt",
    preflightTitle: "Pre-Check And Submit",
    preflightDescription: "The server probes whether the AI Engineers still exist. If some targets fail, you can confirm and skip them.",
    canSubmit: "Ready To Submit",
    cannotSubmit: "Not Ready To Submit",
    serverPrecheck: "The Server Runs The Hard Pre-Check After Submission",
    highVolumeConfirm: "High-Volume Confirmation: Type The Run Name “{name}”",
    highVolumeConfirmAria: "High-Volume Confirmation Run Name",
    cancelledDraftKept: "Cancelled; the current draft is kept",
    nameMismatch: "The run name does not match, so no run was created",
  },
  production: {
    pageDescription: "Sync system records from AI Engineer and curate candidate questions before publishing them to the question bank.",
    projectPrefix: "Project: {name}",
    tabsAria: "Data Sync Workspace",
    tabCandidates: "Candidate Questions",
    tabSync: "Sync Configuration",
    loadingWorkspace: "Loading The Production Conversation Workspace",
    loadingWorkspaceHint: "Confirming the project and its data boundary.",
    feedback: {
      all: "All Feedback",
      negative: "Thumbs Down",
      positive: "Thumbs Up",
      none: "No Feedback",
    },
    duration: {
      seconds: "{value}s",
      minutes: "{value} min",
      hours: "{value} h",
      days: "{value} d",
    },
    totalDuration: "Total Duration",
    elapsed: "Elapsed {value}",
    remaining: "About {value} Remaining",
    remainingUnknown: "Remaining: Not Estimable Yet",
    tenantReadFailed: "Failed To Read Tenants: {message}",
    agentReadFailed: "Failed To Read AI Engineers: {message}",
    capabilityOk: "History Read Capability Check Passed",
    capabilityUnavailable: "History Read Capability Is Currently Unavailable",
    capabilityFailed: "Capability Check Failed: {message}",
    previewFailed: "Preview Failed: {message}",
    syncJobCreated: "Sync batch created; the worker will process it from the snapshot",
    syncJobCreateFailed: "Failed To Create The Sync Batch: {message}",
    policyUpdated: "Filter Policy Updated And A New Version Created",
    policySaved: "Filter Policy Saved",
    policySaveFailed: "Failed To Save The Policy: {message}",
    policyDisabled: "Filter policy disabled; existing sync batches are unaffected",
    policyDisableFailed: "Failed To Disable The Policy: {message}",
    filterConversation: "Filter Conversations",
    filterHint: "The preview reads the remote system only; creating a sync batch happens after you confirm.",
    savedPolicy: "Saved Policy",
    temporaryFilter: "Temporary Filter",
    temporaryFilterSuffix: " · Temporary Filter",
    policyVersionSuffix: " · Policy V{version}",
    connectionHint: "Only enabled connections that declare history_read are listed; tenants load automatically after selection",
    connectionLabel: "Connection",
    connectionAria: "Connection",
    pleaseSelect: "Please Select",
    selectConnectionFirst: "Select A Connection First",
    loadingTenants: "Loading tenants…",
    readFailedRetry: "Read Failed, You Can Retry",
    selectTenant: "Select A Tenant",
    noTenants: "This Connection Has No Tenant",
    autoLoadFromConnection: "Loaded Automatically With The Connection",
    retryTenants: "Retry Loading Tenants",
    agentOptional: "AI Engineer (Optional)",
    selectTenantFirst: "Select A Tenant First",
    loadingAgents: "Loading AI Engineers…",
    autoLoadFromTenant: "Loaded Automatically With The Tenant",
    retryAgents: "Retry Loading AI Engineers",
    userFeedback: "User Feedback",
    startTime: "Start Time",
    endTime: "End Time",
    scope: "Ownership Scope",
    scopeMine: "Mine",
    scopeShared: "Shared",
    conversationStatus: "Conversation Status",
    allStatuses: "All Statuses",
    statusActive: "Active",
    statusRunning: "Running",
    statusCompleted: "Completed",
    statusError: "Error",
    sourceDomain: "Source Domain",
    sourceDomainHint: "Filter by the user's email domain; entering web/api usually returns 0 results",
    blankMeansAll: "Blank = All",
    syncLimit: "Sync Limit",
    syncLimitAria: "Sync Count",
    syncLimitHint: "Maximum Number Of Conversations Added By This Batch",
    policyName: "Policy Name",
    policyNamePlaceholder: "e.g. Thumbs-down conversations of the last 7 days",
    disable: "Disable",
    updatePolicy: "Update Policy",
    saveAsPolicy: "Save As Policy",
    historyReadPrefix: "History Read: ",
    notVerified: "Not Verified Yet",
    checkCapability: "Check Connection Capability",
    verifyHistoryReadFirst: "Verify the History Read capability of the current connection and tenant first",
    previewing: "Previewing…",
    previewSync: "Preview Sync",
    noHistoryReadConnection: "This project has no usable history_read connection. Configure the capability in the connection settings first.",
    disablePolicyTitle: "Disable Filter Policy “{name}”?",
    unnamedPolicy: "Unnamed Policy",
    disablePolicyDescription: "Once disabled the policy disappears from the dropdown and cannot create new batches; existing batches and candidates are unaffected.",
    currentVersion: "Current Version: V{version}",
    disablePolicyIrreversible: "Disabling cannot be undone; save a new policy if you need one again.",
    disablePolicyConfirm: "Disable The Policy",
    disabling: "Disabling…",
    syncPreview: "Sync Preview",
    syncPreviewHint: "Confirm the scope before creating a batch.",
    noPreview: "No Preview Yet",
    noPreviewHint: "Choose the filters and click “Preview sync”.",
    remoteEstimate: "Remote Estimate",
    discovered: "Discovered",
    alreadySynced: "Already Synced",
    evalSkip: "Evaluation Skipped",
    evalSkipTitle: "Conversations Started By The Evaluation Platform (Including Retries)",
    plannedNew: "Planned New",
    incompleteWindows: "{count} time windows could not be fully enumerated.",
    confirmSync: "Sync {count} Conversations",
    jobStatusUpdated: "Batch Status Updated",
    actionFailed: "Action Failed: {message}",
    loadingJobs: "Loading Sync Batches",
    loadingJobsHint: "Reading batch status and statistics.",
    jobsLoadFailed: "Sync Batches Could Not Be Loaded",
    syncJobs: "Sync Records",
    syncJobsHint: "The connection revision and filter snapshot are frozen at creation. Sorted by creation time descending; column headers offer no filtering or sorting.",
    jobsTableLabel: "Production Sync Batches",
    column: {
      createdAt: "Created At",
      processed: "Processed",
      duration: "Duration",
      candidates: "Candidates",
      duplicate: "Deduplicated",
      failed: "Failed",
      incompleteWindows: "Incomplete Windows",
      filterSummary: "Filter Summary",
      attempts: "Attempts",
      error: "Error",
      candidate: "Candidate",
      turns: "Turns",
      lastEdited: "Last Edited",
      conversation: "Conversation",
    },
    processedTitle: "Processed = Conversations (Succeeded/Skipped + Failed) · Remote Hits {remote} · Limit {limit} · Candidates On The Right",
    dateRange: "{from} to {to}",
    viewJobCandidates: "View Candidates Of This Batch",
    collapse: "Collapse",
    retryFailed: "Retry Failed Items",
    noJobs: "No Sync Batch Yet",
    noJobsHint: "Preview and confirm a sync under “Advanced · sync samples” first.",
    loadingJobDetail: "Loading Batch Details",
    loadingJobDetailHint: "Reading the frozen snapshot and per-item results.",
    jobDetailFailed: "Batch Details Could Not Be Loaded",
    jobHeading: "Batch {id}",
    createdBy: "Created By",
    startedAt: "Started",
    finishedAt: "Finished",
    authTenant: "Authenticated Tenant",
    viewSnapshot: "View The Full Filter Snapshot And Window State",
    snapshotTitle: "Filter Snapshot Of Batch {id}",
    snapshotDescription: "The filter conditions and window enumeration state frozen when the batch was created, read-only.",
    jobItemsTableLabel: "Batch Conversation Processing Details",
    noJobItems: "No Conversation Details Yet",
    noJobItemsHint: "This is normal while the batch is queued; the worker records each item once it starts.",
    cancelJobTitle: "Cancel Sync Batch {id}?",
    cancelJobDescription: "The worker stops picking up the remaining {count} conversations. Processed items and generated candidates are kept.",
    cancelJobProcessed: "Processed {done} / {target} Conversations",
    cancelJobCandidates: "{count} Candidates Generated",
    cancelJobIrreversible: "Cancelling cannot be undone; create a new batch to cover the remaining conversations.",
    cancelJobConfirm: "Cancel The Batch",
    cancelling: "Cancelling…",
    bulk: {
      reject: "reject",
      markDuplicate: "Mark As Duplicate",
      addTags: "Add Tags",
    },
    bulkPartial: "{label}: {ok} Succeeded, {failed} Failed",
    bulkDone: "Bulk {label} Applied To {count} Candidates",
    bulkFailed: "Bulk Action Failed: {message}",
    adoptPartial: "Adopted {ok} Production Answers, {failed} Skipped Or Failed",
    adoptDone: "Adopted {count} production answers in bulk; open the editor to review",
    unnamedCandidate: "Unnamed Candidate",
    loadingCandidates: "Loading Candidates",
    loadingCandidatesHint: "Reading candidates and their completeness status.",
    candidatesLoadFailed: "Candidates Could Not Be Loaded",
    queueTitle: "Pending Queue",
    metricsAria: "Question Bank Processing Metrics",
    pendingTitle: "Pending And In-Progress Candidates",
    pending: "Pending",
    viewPending: "View Pending Candidates",
    promotionRate: "Question-Bank Publication Rate {value}%",
    promoted: "Published To Question Bank",
    viewPromoted: "View Published Candidate Questions",
    jobCount: "{count} Sync Batches",
    loadingMetrics: "Loading Operational Metrics",
    synced: "Synced",
    viewJobs: "View Sync Batches",
    medianDuration: "Median Duration",
    medianDurationTitle: "Median Time From Sync To Manual Promotion",
    searchCandidates: "Search Candidates",
    searchPlaceholder: "Source Question, Answer, Feedback Or Draft Content",
    candidateStatus: "Candidate Status",
    statusActiveQueue: "Pending + Editing",
    editing: "Editing",
    rejected: "Rejected",
    duplicate: "Duplicate",
    completeness: "Completeness",
    completeReady: "Ready To Publish",
    missingQuestion: "Incomplete Question",
    missingGroundTruth: "Missing Ground Truth",
    attachmentUnresolved: "Attachment Pending",
    needsWork: "Needs Work",
    feedbackLabel: "Feedback",
    feedbackType: "Feedback Type",
    syncJob: "Sync Batch",
    allJobs: "All Batches",
    moreFilters: "More Filters",
    moreFiltersCount: " ({count} active)",
    ratingLevel: "Source Rating",
    allRatings: "All Ratings",
    stars: "{count} stars",
    editedFilter: "Edited",
    editedFilterAria: "Edited State",
    clearSelection: "Clear Selection",
    editedYes: "Edited",
    editedNo: "Not Edited",
    editor: "Editor",
    editorPlaceholder: "Name Or Account",
    syncedFrom: "Synced From",
    syncedTo: "Synced To",
    sourceCreatedFrom: "Production Conversation Time From",
    sourceCreatedTo: "Production Conversation Time To",
    editedFrom: "Last Edited From",
    editedTo: "Last Edited To",
    bulkAria: "Bulk Actions On Candidates",
    selectedCount: "{count} selected",
    bulkScopeHint: "Selection persists across pages; bulk actions apply to every selected item ({count} on this page).",
    tags: "Tags",
    adopting: "Adopting…",
    adoptBulk: "Adopt Production Answers In Bulk",
    duplicateTarget: "Duplicate Target",
    duplicateTargetType: "Duplicate Target Type",
    candidateId: "Candidate ID",
    questionId: "Question ID",
    duplicateTargetId: "Duplicate Target ID",
    duplicateTargetPlaceholder: "Enter A Target ID In The Same Project",
    bulkReject: "Bulk Reject",
    sortNote: "Default order: thumbs-down first → shorter conversations → most recently synced (fixed; no column filtering or sorting)",
    candidatesTableLabel: "Production Candidates",
    selectPageAria: "Select the editable candidates on this page (selection persists across pages)",
    selectCandidateAria: "Select Candidate {title}",
    turnCount: "{count} turns",
    noCandidates: "No Matching Candidate",
    noCandidatesFiltered: "The current filter matched no candidate; clear the filters and retry.",
    noCandidatesHint: "Sync production conversations first and candidates are generated automatically.",
    rejectTitle: "Reject The {count} Selected Candidates?",
    rejectDescription: "Rejected candidates reach a terminal state and can no longer be promoted; the production source snapshot is kept.",
    affectedCount: "{count} Affected (Selected Across Pages, {onPage} On This Page)",
    rejectIrreversible: "Rejecting cannot be undone; use it for junk items or conversations that cannot be processed.",
    rejectConfirm: "Reject In Bulk",
    rejecting: "Rejecting…",
    markDuplicateTitle: "Mark {count} Candidates As Duplicates?",
    markDuplicateDescription: "Marked candidates reach the duplicate terminal state and cannot be promoted.",
    duplicateTargetSummary: "Duplicate Target: {type} {id}",
    candidateLabel: "Candidate",
    questionLabel: "Question",
    markDuplicateConfirm: "Mark As Duplicate",
    marking: "Marking…",
  },
  candidate: {
    draftSaved: "Candidate Draft Saved · {time}",
    saveFailed: "Save Failed: {message}",
    splitDone: "Split into separate candidates by turn; opening the new candidate {id}",
    splitFailed: "Split Failed: {message}",
    rejected: "Candidate rejected; moved to the next one if available",
    rejectFailed: "Reject Failed: {message}",
    markedDuplicate: "Candidate marked as duplicate; moved to the next one if available",
    markDuplicateFailed: "Marking As Duplicate Failed: {message}",
    promoted: "Candidate Question Published To The Question Bank",
    promoteFailed: "Publishing Failed: {message}",
    noSourceAnswer: "This turn has no production answer to fill in",
    filledTurn: "Filled In The Production Answer Of Turn {index}",
    filledAll: "Filled in {count} production answers; review and save",
    nothingToFill: "No Production Answer To Fill In",
    terminalRejected: "This candidate was rejected; continue with the next one or go back to the queue.",
    terminalDuplicate: "This candidate was marked as duplicate; continue with the next one or go back to the queue.",
    terminalPromoted: "This candidate question was published; open the question or continue with the next one.",
    terminalGeneric: "This candidate reached a terminal state.",
    needOneTurn: "Keep at least one turn with a question and ground truth.",
    completeTurns: "Complete the question and ground truth of every turn.",
    confirmAttachment: "Confirm that the attachment dependency has been handled.",
    saveBeforePromote: "Save the draft before publishing it to the question bank.",
    handleDuplicateCandidate: "Mark it as duplicate or handle the other candidate first.",
    handleDuplicateQuestion: "Mark it as duplicate, or create a new version from the existing question.",
    loadingTitle: "Loading The Candidate Editor",
    loadingDescription: "Reading the immutable source and the candidate draft.",
    openFailed: "The Candidate Cannot Be Opened",
    backToCandidates: "Back To Candidates",
    backToQueue: "Back To The Candidate Queue",
    backToQueueShort: "Back To Queue",
    editorTitle: "Candidate Editor",
    editorDescription: "The production source on the left is read-only; only the right-hand content becomes ground truth in the question bank.",
    queuePosition: "Queue {index} / {total}",
    queueNavAria: "Candidate Queue Navigation",
    previous: "Previous",
    next: "Next",
    viewQuestion: "View The Question",
    terminalHint: "Stale validation and editing controls are cleared. The next action is in the bottom bar; continuing the queue is preferred over staying on a read-only page.",
    sourceTitle: "Production Source",
    sourceHint: "The Immutable Conversation Snapshot Saved At Sync Time",
    readOnly: "Read-only",
    agentPrefix: "AI Engineer: {name}",
    userPrefix: "User: {name}",
    targetRound: "Target Round: {value}",
    targetRoundHint: "The target round is the one that matched the feedback at sync time; multi-turn conversations still show the full dialogue for reference.",
    targetRoundNote: " (feedback round; the conversation may have more turns)",
    fillAllGt: "Fill Ground Truth For All Turns",
    fillAllHint: "You still need to review and save after filling.",
    userRound: "User · Round {round}",
    productionAnswer: "Production Answer (For Reference Only, Not Ground Truth)",
    fillGt: "Fill Ground Truth",
    attachmentCount: "Contains {count} Attachment References",
    draftTitle: "Candidate Draft",
    draftHint: "Rewrite the question and add a reference answer for every turn.",
    splitByTurn: "Split Into Single-Turn Items",
    titleOptional: "Title (Optional)",
    turnOrdinal: "Turn {index}",
    deleteTurn: "Delete Turn {index}",
    question: "Question",
    groundTruth: "Ground Truth",
    gtPlaceholder: "Enter The Reference Answer For This Turn",
    addTurn: "Add A Turn",
    tags: "Question Tags",
    note: "Processing Note",
    attachmentHandled: "Attachment Dependency Handled Manually",
    attachmentHandledHint: "Confirm the question is understandable without the original attachment, or that the needed information is written into the question.",
    duplicateFound: "An Identical Question Was Found",
    officialQuestionPrefix: "Question Bank: ",
    candidatePrefix: "Candidate: ",
    duplicateTerminalHint: "This candidate is in a terminal state; the duplicate link is kept only for traceability.",
    duplicateCandidateHint: "Open the comparison and mark it as duplicate, or handle the other candidate first, before promoting.",
    duplicateQuestionHint: "Choose one: mark as duplicate, or create a new version from the existing question.",
    markDuplicate: "Mark As Duplicate",
    newVersionFromQuestion: "Create A New Version From The Existing Question",
    terminalNoValidation: "Terminal State: No Further Validation Needed",
    validationPassed: "Completeness Check Passed",
    unsaved: "Unsaved Changes",
    savedAt: "Saved At {time}",
    persistedAt: "Persisted · {time}",
    reject: "Reject",
    saveDraft: "Save Draft",
    promoting: "Publishing…",
    promote: "Publish To Question Bank",
    rejectTitle: "Reject This Candidate?",
    rejectDescription: "A rejected candidate reaches a terminal state and cannot be promoted; the production source snapshot is kept. You move to the next candidate if there is one.",
    candidateSummary: "Candidate: {name}",
    rejectHint: "Use it for junk items or conversations that cannot be processed; rejecting cannot be undone.",
    rejectConfirm: "Reject",
    splitTitle: "Split Into Separate Candidates By Turn?",
    splitDescription: "New independent candidate copies are generated from the current multi-turn draft (about {count} turns). The original is kept; process both separately so identical content does not stay unhandled.",
    splitHint: "The new candidate opens after splitting; the original stays in the queue.",
    splitConfirm: "Split",
    splitting: "Splitting…",
    duplicateTitle: "Mark As Duplicate?",
    duplicateDescription: "The candidate reaches the duplicate terminal state and will not be promoted. You move to the next candidate if there is one.",
    duplicateTargetQuestion: "Duplicate Target (Question): {name}",
    duplicateTargetCandidate: "Duplicate Target (Candidate): {name}",
    duplicateCompareHint: "Open the duplicate for comparison before confirming.",
    discardTitle: "Discard The Unsaved Draft Changes?",
    discardListDescription: "This candidate has unsaved changes; going back to the queue discards them.",
    discardNeighborDescription: "This candidate has unsaved changes; switching to another candidate discards them.",
    discardHint: "Choose “Keep editing” to save the draft before leaving.",
    discardConfirm: "Discard And Leave",
    keepEditing: "Keep Editing",
  },
  questionBank: {
    globalDescription: "Search questions and current versions across projects; create, import and edit inside the owning project's question bank.",
    globalProjectFilterAria: "Filter Question Banks By Owning Project",
    globalSummary: "{count} questions",
    globalFilteredHint: "Only questions matching the current filters are shown.",
    globalNoQuestionsHint: "No questions are available yet; create or import them inside a project's question bank.",
    globalQuestionId: "Question ID",
    globalCurrentVersion: "Current Version",
    openInProject: "View In Project",
    backToAllBanks: "Back To All Question Banks",
    manageProjects: "Manage Projects",
    pickProjectTitle: "Pick A Project Question Bank",
    pickProjectHint: "Questions cannot be created or imported without a project context.",
    loadingProjects: "Loading Projects",
    projectsLoadFailed: "Failed To Load Projects",
    projectFallbackDescription: "Maintain The Questions And Versions Of This Project",
    questionCount: "{count} questions",
    noProjects: "No Project Available Yet",
    noProjectsHint: "Create a project first, then add questions to its bank.",
    pageTitle: "{name} · Question Bank",
    pageDescription: "Tick questions to compose a suite, or import an Excel file. Editing creates a new version; existing suites keep referencing the original one.",
    backToSuite: "Back To Suite",
    backToContent: "Back To Content",
    tagManager: "Tag Manager",
    import: "Import",
    newQuestion: "New Question",
    editQuestion: "Edit Question",
    importStatus: {
      uploaded: "Uploaded",
      parsing: "Parsing",
      review: "Awaiting Review",
      awaitingSuiteName: "Awaiting Suite Name",
      committing: "Writing",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    currentVersion: "Current Version",
    historicalVersion: "Historical Version",
    multiTurn: "Multi-Turn · {count} Turns",
    multiTurnShort: "Multi·{count}",
    singleTurn: "Single Turn",
    conversationFilter: "Conversation",
    conversationFilterAria: "Filter By Conversation Type",
    conversationAll: "All",
    conversationMulti: "Multi-turn",
    turnsFilter: "Turns",
    turnsFilterAria: "Filter By Turn Count",
    turnsFilterAll: "All Multi-Turn Buckets",
    turnBucket: {
      exact: "{count} turns",
      fivePlus: "5+ turns",
    },
    activeFiltersAria: "Active Filters",
    activeFilter: {
      search: "Search: {query}",
      status: "Status: {status}",
      conversation: "Conversation: {mode}",
      turns: "Turns: {turns}",
      tag: "Tag: {tag}",
    },
    removeFilter: "Remove Filter {label}",
    moreTags: "+{count}",
    suiteBarHintOutsideFilter: "{count} Selected ({outside} Outside The Current Filter)",
    keepVisibleSelection: "Keep Visible Only",
    clearSelection: "Clear Selection",
    versionTurnsAria: "Conversation Turns Of V{version}",
    turnOrdinal: "Turn {index}",
    turnId: "Turn ID: {id}",
    userInput: "User Input",
    groundTruth: "Ground Truth",
    actionFailedRetry: "The Action Failed, Please Retry Later",
    suiteNameRequired: "Enter A Suite Name",
    suiteNameTooLong: "The Suite Name Cannot Exceed {max} Characters",
    suiteNameDuplicate: "A suite with this name already exists in the project",
    selectAtLeastOne: "Select At Least 1 Question First",
    importLoadFailed: "Failed To Load The Import Record: {message}",
    questionDisabled: "Question {code} Disabled",
    questionEnabled: "Question {code} Enabled",
    disableFailed: "Failed To Disable The Question: {message}",
    enableFailed: "Failed To Enable The Question: {message}",
    tagReused: "Reused An Existing Tag",
    tagCreated: "Project Tag Created And Selected",
    editorRequiredFields: "Fill in the question ID and the user input and ground truth of every turn",
    savedAsNewVersion: "Question Saved As A New Version",
    questionAdded: "Question Added To This Project",
    saveFailed: "Failed To Save The Question: {message}",
    historyLoadFailed: "Failed To Load The Version History: {message}",
    targetProject: "Target Project",
    copied: "Copied to {name}; later versions stay independent",
    copyFailed: "Failed To Copy The Question: {message}",
    deletedWithSuites: "Question deleted; {count} existing suites keep the original version",
    deleted: "Question Deleted From This Project's Bank",
    deleteFailed: "Failed To Delete The Question: {message}",
    suiteCreated: "Suite created: {name}. You can start an evaluation next.",
    suiteCreateFailed: "Failed To Create The Suite: {message}",
    pickXlsx: "Choose an .xlsx file",
    parsedWithConflicts: "Parsing Finished With {count} Conflicts To Resolve",
    parsedReady: "Parsing finished; the import can be submitted",
    parseFailed: "Failed To Parse The Import File: {message}",
    importCommitted: "Import Committed To This Project",
    suiteNameConflict: "The suite name conflicts; change it and continue",
    importIntentConflict: "This import was already committed with another resolution set; the page now shows the database state",
    commitFailed: "Failed To Commit The Import: {message}",
    composeHint: "Compose mode: tick questions, then enter a suite name at the bottom and create it; the suite pins the current versions.",
    locatedPrefix: "Located Question ",
    locatedSuffix: " and cleared the filters that could hide it.",
    closeLocation: "Close Locator",
    questionMissing: "The question in the link is not in this project or has been deleted.",
    closeNotice: "Close Notice",
    tagFilters: "Tag Filters",
    recentImports: "Recent Imports",
    rowCount: "{count} rows",
    noImports: "No Import Record Yet",
    newImport: "New Import",
    searchPlaceholder: "Search Question ID Or Question",
    clearSearch: "Clear Search",
    questionStatus: "Question Status",
    questionStatusFilterAria: "Filter By Question Status",
    statusActive: "Enabled",
    statusDisabled: "Disabled",
    statusArchived: "Archived",
    refreshBank: "Refresh The Question Bank",
    listSummary: "Showing {range} Of {total}",
    selectedSuffix: " · {count} selected",
    listSummaryTail: " · Select-All Applies To This Page Only · Sorted By {sort}",
    defaultSort: "Update Time, Newest First",
    sortLabel: "Sort",
    sortAria: "Question List Sort",
    sortContentUpdatedDesc: "Recently Edited",
    sortCodeAsc: "Question ID Ascending",
    selectedCount: "{count} selected",
    loadingBank: "Loading The Question Bank",
    bankLoadFailed: "Failed To Load The Question Bank",
    noMatching: "No Matching Question",
    noMatchingHint: "The filters are kept; clear them and retry.",
    noQuestions: "This Project Has No Question Yet",
    noQuestionsHint: "Add a question, import an Excel file, or copy from another project.",
    importExcel: "Import Excel",
    selectPageAria: "Select All Enabled Questions On This Page",
    selectPageTitle: "Only Enabled Questions On This Page Are Selected",
    selectQuestion: "Select {code}",
    disabledCannotSelect: "The question is disabled and cannot join a suite",
    fromProduction: "From Production",
    disableQuestionAria: "Disable Question {code}",
    enableQuestionAria: "Enable Question {code}",
    disabling: "Disabling…",
    enabling: "Enabling…",
    history: "History",
    copyToProject: "Copy To Another Project",
    noOtherProjects: "No Other Project Available",
    suiteBarAria: "Create A Suite From The Selected Questions",
    suiteBarHint: "{count} questions selected; enter a name to create the suite, which pins the current versions.",
    suiteNameLabel: "Suite Name *",
    suiteNamePlaceholder: "e.g. Inventory regression suite",
    suiteNameValid: "The name is valid; a suite with {count} questions can be created",
    createSuiteButton: "Create Suite ({count})",
    importTitle: "Import Into This Project",
    importDescription: "One template covers single- and multi-turn questions: questions carry tags, turns carry user input and ground truth.",
    importProgress: "{phase}, {elapsed} elapsed",
    parsingFile: "Parsing The File",
    writing: "Writing",
    importMode: "Import Mode",
    importModeAria: "Import Mode",
    importModeBank: "Question Bank Only",
    importModeSuite: "Import And Create A Suite",
    importSuiteNameAria: "Suite Name For The Import",
    suiteNameHelp: "At most {max} characters, unique within the project.",
    excelFile: "Excel File",
    pickExcelAria: "Choose An Excel File",
    downloadTemplate: "Download The Excel Template",
    reparse: "Re-Parse And Check",
    parseStep: "1. Parse and check",
    unitQuestions: "questions",
    unitTurns: "turns",
    unitMultiTurn: "multi-turn",
    unitNew: "new",
    unitReused: "reused",
    unitBlocking: "Blocking Conflicts",
    recoveryTitle: "Questions Written, Suite Not Created Yet",
    recoveryHint: "The original name “{name}” is taken. Enter a new suite name above to continue; questions will not be created twice.",
    committingTitle: "Questions Already Written",
    committingHint: "Continuing with the locked name “{name}” to create the suite; questions will not be created twice.",
    suiteNameRecheck: "Change the suite name above; the backend validates it again on submit.",
    column: {
      codeVersion: "Question ID / Version",
      conversation: "Conversation",
      row: "Row",
      codeQuestion: "Question ID / Question",
      suggestion: "Suggestion",
      conflict: "Conflict",
      resolution: "Resolution",
    },
    action: {
      create: "Create",
      reuse: "Reuse",
      updateTags: "Update Tags",
      review: "Needs Review",
    },
    noConflict: "No Conflict",
    autoReuse: "Auto Reuse",
    autoUpdateTags: "Auto Update Tags",
    autoCreate: "Auto Create",
    footerCompleted: "The import is complete.",
    footerNeedNewName: "Enter a name different from the original one.",
    footerNameAvailable: "The new name is available; you can create the suite.",
    footerCommitting: "Questions are written; you can create the suite and finish the import.",
    footerLocked: "The current import state cannot continue; refresh the record or contact an administrator.",
    footerUnresolved: "{count} blocking conflicts still need a resolution.",
    footerReady: "All blocking conflicts are resolved; ready to submit.",
    viewSuite: "View Suite",
    continueWithNewName: "Continue With The New Name",
    continueImport: "Finish The Import",
    commitStep: "3. Confirm and write",
    editorDescription: "One turn means single-turn; multiple turns run in order. Tags apply to the whole question and scoring rules are configured in the evaluation.",
    saveNewVersion: "Save New Version",
    createQuestion: "Create Question",
    codeHelp: "The unique identifier of the question inside the project.",
    codeRequired: "Question ID is required.",
    codeRequiredLabel: "Question ID *",
    moveUp: "Move Up",
    moveDown: "Move Down",
    moveUpAria: "Move Turn {index} Up",
    moveDownAria: "Move Turn {index} Down",
    userInputRequired: "User Input *",
    userInputHelp: "The input sent to the system under test in this turn.",
    userInputRequiredError: "User input is required.",
    groundTruthRequired: "Ground Truth *",
    groundTruthHelp: "The expected answer used as the scoring reference.",
    groundTruthRequiredError: "Ground truth is required.",
    hidePreview: "Hide Preview",
    showPreview: "Conversation Preview",
    currentSingleTurn: "Currently A Single-Turn Question",
    currentMultiTurn: "Currently A {count}-Turn Conversation",
    previewUserTurn: "User · Turn {index}",
    notFilled: "(Not Filled In Yet)",
    loadingTags: "Loading Available Tags",
    tagsLoadFailed: "Tags failed to load; the question content is kept.",
    discardTitle: "Discard The Unsaved Changes?",
    discardDescription: "Closing the editor loses the question ID, turns and tags you entered.",
    discardConfirm: "Discard Changes",
    historyTitle: "{code} · Version History",
    historyDescription: "Every edit creates a read-only historical version, so suite references never drift.",
    loadingHistory: "Loading History",
    copyTitle: "Copy The Question To Another Project",
    copyDescription: "Only the current version is copied; the target project gets an independent question and version history.",
    copyQuestion: "Copy Question",
    targetProjectRequired: "Target Project *",
    pleaseSelectProject: "Select A Project",
    targetCodeRequired: "Target Question ID *",
    targetCodeHelp: "The question identifier inside the target project.",
    targetCodeRequiredError: "Target question ID is required.",
    disableTitle: "Disable This Question?",
    disableDescription: "New test tasks skip the question afterwards; existing suites, versions and historical runs are not deleted. Runs already created are unaffected.",
    disableNote: "After disabling, the question is also removed from the current suite selection; you can re-enable it at any time.",
    disableConfirm: "Disable",
    deleteTitle: "Delete This Question?",
    deleteDescription: "The question is hidden from this project's bank, but version history and references in existing suites and reports are kept.",
    deleteNote: "After deletion the question cannot be used in new suites; existing evaluation data is unaffected.",
    deleteConfirm: "Delete Question",
    deleting: "Deleting…",
    resolveConflictAria: "Resolve Conflict {id}",
    resolution: {
      reuse: "Reuse The Bank Version",
      createVersion: "Create A New Question Version",
      newQuestion: "Import As A New Question",
      skip: "Skip This Row",
    },
  },
  reportComparison: {
    back: "Back To Reports", scope: "Fixed Test Scope", multipleSuites: "Multiple Suites", selectedCount: "{count} Reports Selected", adjust: "Adjust Reports",
    tabsAria: "Report Comparison Views", tabs: { overview: "Overview", questions: "Question Differences", snapshots: "Runtime Snapshots" },
    viewAria: "Chart View", horizontal: "Report Comparison", timeline: "Timeline", loading: "Loading Report Snapshots…", needTwo: "Select at least 2 available reports to compare.",
    metrics: { platform: "Platform Stability", platformHint: "Successful Return Rate By Report", stability: "Cross-Report Answer Consistency", stabilityHint: "Answer Consistency Across Reports From The Same Test Suite", experience: "User Experience", experienceHint: "Complete-Answer P50", cost: "Cost", costHint: "Tokens Per Successful Question", quality: "Answer Quality", qualityHint: "Quality Score Of Valid Answers In Each Report" },
    higherBetter: "Higher Is Better", lowerBetter: "Lower Is Better", latestChanges: "Latest Question Changes", latestChangesHint: "Last Two Adjacent Reports By Time", questionDifferences: "Key Question Differences", questionDifferencesHint: "Highest-To-Lowest Score Across Selected Reports", viewAll: "View All", spread: "Spread", noCommonQuestions: "No common valid questions are available for comparison.",
    question: "Question", snapshotField: "Snapshot Field", releaseVersion: "Release Version", releaseShort: "Release", agentVersion: "AI Engineer Version", promptVersion: "Prompt Version", agent: "AI Engineer", environment: "Environment", repeat: "Repeats", notRecorded: "Not Recorded",
    selectorTitle: "Adjust Compared Reports", selectorDescription: "{count} available reports in this scope. Select at least 2; there is no fixed maximum.", apply: "Apply Selection", searchPlaceholder: "Search Reports, AI Engineer, Or Version", historyAction: "View History", compareSelected: "Compare Selected ({count})",
    questionTableHint: "Click a question row for answer details · All {count} reports are shown · Spread is the rightmost column", questionTableScrollHint: "Scroll Horizontally For More Reports", questionDetail: "Question Comparison Detail", questionDetailDescription: "Compare answers across {count} reports. Repeated answers can be switched independently in each report.", previousQuestion: "Previous", nextQuestion: "Next", standardAnswer: "Standard Answer", repeatAnswers: "Repeated Answers", repeatNumber: "Answer {count}", duration: "Duration", judgeFindings: "Judge Findings",
  },
  crossEvaluation: {
    title: "Cross Evaluation", description: "Run the same evaluation questions across signed-in AI platforms. Each question may contain one or multiple consecutive turns.",
    navAria: "Cross Evaluation Navigation", nav: { tasks: "Evaluation Tasks", sources: "Data Sources" }, configureSources: "Configure Sources", newBatch: "New Evaluation",
    createTitle: "New Cross-Platform Evaluation", createHint: "Choose a project suite and at least two web platforms. Turns within one question share a conversation; separate questions use separate conversations.",
    batchName: "Evaluation Name", batchNamePlaceholder: "Example: Cross-Platform Contract Review Benchmark", selectProject: "Select Project", selectSuite: "Select Suite", selectSources: "Select Platforms", evaluationProfile: "Evaluation Profile", selectProfile: "Select Evaluation Profile", loadingProfiles: "Loading Evaluation Profiles…",
    noActiveSources: "No active source is available. Configure the data source connection and web platforms first.",
    creating: "Creating…", start: "Start Evaluation", created: "Created “{name}”", createFailed: "Could Not Create Evaluation: {message}",
    blockerName: "Enter an evaluation name.", blockerProject: "Select a project.", blockerSuite: "Select a test suite.", blockerProfile: "Select an evaluation profile for real Judge scoring.", blockerSources: "Select at least 2 real sources; {count} selected.", readySources: "{count} real sources selected. The evaluation is ready to start.",
    batchList: "Evaluation Tasks", batchListHint: "Track real collection progress, partial failures, and reports.", loading: "Loading Evaluations", loadingHint: "Reading immutable task snapshots and platform states.",
    loadFailed: "Could Not Load Evaluations", emptyTitle: "No Evaluation Tasks Yet", emptyHint: "Configure sources, then create an evaluation with ordered questions.",
    filteredEmptyTitle: "No Results For These Filters", filteredEmptyHint: "Adjust the project, status, platform, or keyword filters.", searchPlaceholder: "Search Name, Task ID, Or Suite ID", sortLabel: "Sort", sortNewest: "Created: Newest First", sortOldest: "Created: Oldest First", tableAria: "Cross Evaluation Task List", batch: "Evaluation", progress: "Run Progress", progressValue: "{completed} / {total} Complete",
    sourcesTitle: "Data Sources", sourcesDescription: "Configure the browser research agent, target platform sessions, and versioned collection instructions.",
    configLoading: "Loading Collection Configuration", configLoadingHint: "Reading Connections, sources, and the active prompt.",
    connectionTitle: "Browser Research Agent", connectionHint: "The agent controls authenticated browser sessions through a write-only API key that never appears in read APIs, prompts, snapshots, or logs.",
    connectionName: "Agent Connection Name", apiBaseUrl: "Agent API Base URL", apiKey: "API Key", apiKeyHint: "Submitted only when saving; afterwards the UI only shows whether a key is configured.", apiKeySavedPlaceholder: "Leave Blank To Keep The Saved Key", createConnection: "Add Agent Connection",
    connectionModalTitle: "Create Browser Research Agent Connection", connectionEditTitle: "Edit Browser Research Agent Connection", connectionModalHint: "Platform sources can reuse this agent connection. The API key is stored as a write-only secret.", connectionCountOne: "1 Connection", connectionCount: "{count} Connections", connectionLoadFailed: "Could Not Load Agent Connections",
    connectionCreated: "Agent Connection Created", connectionUpdated: "Agent Connection Updated", connectionCreateFailed: "Could Not Create Agent Connection: {message}", connectionUpdateFailed: "Could Not Update Agent Connection: {message}", connectionDeleted: "Agent Connection Deleted", connectionDeleteFailed: "Could Not Delete Agent Connection: {message}", noConnections: "No Agent Connection Yet", noConnectionsHint: "Create a browser research agent connection before adding platform sources.",
    disableConnection: "Disable", enableConnection: "Enable", connectionDeleteTitle: "Delete This Agent Connection?", connectionDeleteDescription: "“{name}” will be permanently deleted. Remove or reassign every source that uses it first.",
    secretConfigured: "API Key Configured", probe: "Check Connection", probing: "Checking…", probeSuccess: "The Browser Research Agent Is Online", probeWarning: "The Browser Research Agent Is Currently Offline", probeFailed: "Connection Check Failed: {message}",
    probeStatusReady: "Connection Ready", probeDetailReady: "The service, API key, and browser extension have all been verified.",
    probeStatusExtensionOffline: "Browser Extension Not Connected", probeDetailExtensionOffline: "The service and API key are valid. Start or reconnect the browser extension.",
    probeStatusServiceUnreachable: "Agent Service Unreachable", probeDetailServiceUnreachable: "Check the service URL, network, and deployment status, then retry.",
    probeStatusApiKeyMissing: "API Key Not Configured", probeDetailApiKeyMissing: "Configure an API key, then check the connection again.",
    probeStatusApiKeyInvalid: "API Key Invalid", probeDetailApiKeyInvalid: "Update the API key, then check the connection again.",
    probeStatusConfigurationInvalid: "Connection Configuration Invalid", probeDetailConfigurationInvalid: "Review the Agent API URL and connection settings, then retry.",
    probeStatusServiceResponseInvalid: "Unexpected Service Response", probeDetailServiceResponseInvalid: "The service responded with an unexpected payload. Check the service version and API URL.",
    probeStatusServiceError: "Service Request Failed", probeDetailServiceError: "The service responded, but the check failed. Review the service logs, then retry.",
    sourceTitle: "Platform Sources", sourceHint: "Each source defines one target application, its authenticated browser entry point, and platform-specific interaction instructions for the collection agent.",
    sourceName: "Source Name", platformCode: "Platform Code", platformCodeImmutableHint: "Used to match platform login credentials and cannot be changed after creation.", publicUrl: "Platform URL", targetUrl: "Agent Access URL", selectConnection: "Select Data Source Connection", createSource: "Create Source",
    platformLogin: "Platform Login", platformLoginEnabled: "Configure A Login For This Platform", platformLoginOptional: "Optional. The Browser Research Agent stores the credentials securely; this service does not retain a copy.", platformUsername: "Username", platformPassword: "Password", platformPasswordSaved: "Leave Blank To Keep The Saved Password", platformPasswordRequired: "A password is required for a new account or when changing the Agent connection.", platformPasswordOptional: "Leave blank to continue using the saved password.",
    sourceModalTitle: "Create Platform Source", sourceEditTitle: "Edit Platform Source", sourceModalHint: "Define how the browser research agent opens and operates this platform while preserving the shared output contract.", sourceCount: "{count} Sources", sourceNeedsConnection: "An Active Agent Connection Is Required", sourceNeedsConnectionHint: "Every platform source must reference an active browser research agent connection.",
    sourceCreated: "Platform Source Created", sourceUpdated: "Platform Source Updated", sourceDeleted: "Platform Source Deleted", sourceCreateFailed: "Could Not Create Source: {message}", sourceUpdateFailed: "Could Not Update Source: {message}", sourceDeleteFailed: "Could Not Delete Source: {message}", sourceDeleteTitle: "Delete This Platform Source?", sourceDeleteDescription: "“{name}” and its reusable collection tasks will be permanently deleted. Historical evaluation records must be deleted first.", sourceLoadFailed: "Could Not Load Sources", noSources: "No Platform Sources Yet", noSourcesHint: "Add ChatGPT, Gemini, and ContractIQ browser sources.", sourceTableAria: "Cross Evaluation Source List", disableSource: "Disable", enableSource: "Enable",
    interactionPrompt: "Platform Interaction Instructions", interactionPromptHint: "Describe this platform's navigation, selectors by visible meaning, conversation rules, and completion signals. Do not redefine the shared JSON result schema here.",
    advancedConfiguration: "Advanced Configuration", promptTitle: "Collection Prompt", promptHint: "The template drives ordered questions, waits for complete answers, and requests versioned JSON. Every batch freezes the effective template.", promptBody: "Prompt Template",
    promptContractHint: "Keep the six double-brace variables: collection_task_id, source_id, platform_code, source_name, platform_instructions, and questions_json. JSON v2 validates task and platform identity, turn count, order, text, status, and observed timing.",
    promptSaved: "Collection Prompt Saved", promptSaveFailed: "Could Not Save Collection Prompt: {message}", restorePrompt: "Restore Default", promptRestored: "Default Collection Prompt Restored", promptRestoreFailed: "Could Not Restore Prompt: {message}", promptLoadFailed: "Could Not Load Collection Prompt",
    deleteEvaluationTitle: "Delete This Evaluation?", deleteEvaluationDescription: "“{name}” and all of its collection runs, answers, assessments, and events will be permanently deleted.", evaluationDeleted: "Evaluation Deleted", evaluationDeleteFailed: "Could Not Delete Evaluation: {message}",
    backToList: "Back To Evaluations", detailLoading: "Loading Evaluation Detail", detailLoadingHint: "Reading source runs, answers, and status events.", detailLoadFailed: "Could Not Load Evaluation Detail",
    profileSnapshot: "Real Scoring Snapshot: {name} · V{version}",
    detailDescription: "Project {project} · Suite {suite}", stop: "Stop Task", stopping: "Stopping…", stopRequested: "Stop Requested", stopFailed: "Could Not Stop Task: {message}", stopConfirmTitle: "Stop This Evaluation?", stopConfirmDescription: "Active platform collection will be stopped. Answers already captured will remain available.", confirmStop: "Stop Evaluation",
    retryFailedSources: "Retry Failed Platforms", retrying: "Retrying…", retryQueued: "Failed Platforms Requeued", retryFailed: "Retry Failed: {message}", viewReport: "View Report", partialFailureNotice: "Some platforms failed. Successful real answers remain available; failed platforms can be retried independently.",
    statusHint: "Aggregated Batch Status", sourcesCompleted: "Completed Platforms", sourcesFailed: "Failed Platforms", platformResults: "Platform Results", platformResultsHint: "Completed / Partial / Failed", platformTotal: "Selected Platforms", questionsCaptured: "Real Answers", sourceRuns: "Platform Runs", sourceRunsHint: "Each platform runs independently. Every question is linked to one reusable collection task, and every invocation keeps its own execution record.",
    collectionQuestion: "Question {order}", turnCount: "{count} Consecutive Turns", executionDetails: "Execution Details", previousAttempts: "Previous Attempts ({count})", collectionExecutionId: "Collection Execution ID", executionAttempt: "Execution Attempt", mispTaskId: "Collection Task ID", queueEntryId: "Execution Queue ID", attempt: "Worker Attempts", attemptValue: "{current} / {max}", capturedAnswers: "Real Captured Answers", capturedAnswersHint: "Answers are persisted by question and turn, while raw collection output is retained for reparsing.",
    viewRawOutput: "View Raw Output", hideRawOutput: "Hide Raw Output", rawOutputLoading: "Loading Raw Collection Output On Demand…", rawOutputLoadFailed: "Raw Output Load Failed: {message}", rawOutputEmpty: "No Raw Output Is Available For This Execution.", rawOutputSafetyNotice: "Sensitive Data Redacted: {redacted} · Content Truncated: {truncated}",
    answersPending: "Waiting For Real Answers", answersPendingHint: "The browser research agent is completing the platform session. This page refreshes automatically.", noAnswers: "No Answers To Show", noAnswersHint: "Review platform errors and events, then retry failed sources.",
    assessmentStatus: "Scoring Status", assessmentPending: "Waiting For Scoring", assessmentRunning: "Scoring", assessmentSkipped: "No Scorable Answer", assessmentFailed: "Scoring Failed: {message}", assessmentFailedShort: "Scoring Failed",
    answerTableAria: "Cross Evaluation Real Answers", order: "Order", question: "Question", platform: "Platform", answer: "Answer", assessment: "Assessment", emptyAnswer: "No Answer Captured", timingUnavailable: "Observed Timing Unavailable", notAssessed: "Not Assessed",
    events: "Activity History", eventCount: "{count} Events", eventsHint: "Important batch and platform state changes, without API keys.", eventsLoadFailed: "Could Not Load Events", noEvents: "No Events Yet", noEventsHint: "Events appear after the task starts.",
    backToDetail: "Back To Evaluation Detail", reportLoading: "Building Evaluation Report", reportLoadingHint: "Combining questions, platforms, real answers, and assessments.", reportLoadFailed: "Could Not Load Evaluation Report",
    reportTitle: "{name} · Evaluation Report", reportDescription: "Compare real web-platform answers, observed timing, and assessment results by question.", downloadJson: "Download JSON", downloadMarkdown: "Download Report", printPdf: "Print / Save PDF",
    horizontalComparison: "Question-By-Question Comparison", horizontalComparisonHint: "Platform answers are shown side by side. Consecutive turns within a question keep context; separate questions are isolated.", reportEmpty: "No Answers In This Report Yet", reportEmptyHint: "Wait for at least one platform to complete collection.",
    coverage: "Answer Coverage", coverageHint: "{captured} Captured / {expected} Expected", sourceSuccessFailure: "Platforms Succeeded / Failed", successFailureHint: "Based On Real Platform Run Results", averageDuration: "Average Observed Duration", measuredCount: "Based On {count} Answers With Browser-Observed Timing", averageAnswerLength: "Average Answer Length", characterUnit: "Characters In Real Captured Answers Only", averageScore: "Average Quality Score", scoredAnswerCount: "Based On {count} Assessed Answers", reportMatrixAria: "Question By Platform Answer Matrix",
    questionOrder: "Question {order}", groundTruth: "Ground Truth: {answer}", sourceNoAnswer: "This platform did not return an answer for this question.", viewFullAnswer: "View Full Answer", answerDetailTitle: "Answer Detail", answerDetailDescription: "Question {order} · {source}", answerDetailNavigation: "Answer Detail Navigation", previousQuestion: "Previous Question", nextQuestion: "Next Question", answerLength: "Answer Length", answerLengthValue: "{count} Characters", noAnswerDetailHint: "Move to the previous or next question to inspect other captures from this platform.",
  },
};
